import React, { Component } from "react";
import { withApollo } from "react-apollo";
import { withRouter } from "react-router-dom";
import { sortRows, nextSort, SortTh } from "./sortable";
import gql from "graphql-tag";
import * as Cookies from "es-cookie";
import { Modal } from "react-bootstrap";
import ReactApexChart from "react-apexcharts";
import ScopeFilters from "./ScopeFilters";
import ExamComparisonsTab from "./ExamComparisonsTab";
import ExportButtons from "./ExportButtons";
import { exportExcel, exportPdf } from "./misExport";
import { TableShimmer, ModalFormShimmer } from "../preloader/PageCardShimmer";
import { headerBranches } from "./misScope";

const GET_EXAM_REPORTS = gql`
  query($institute_id: Int!, $branch_id: String, $class_id: String, $section_id: String, $stream_id: String) {
    getExamReports(institute_id: $institute_id, branch_id: $branch_id, class_id: $class_id, section_id: $section_id, stream_id: $stream_id) {
      id name className date endDate appeared totalStudents avgPct highPct lowPct passPct errorPct totalQuestions
    }
  }
`;

const GET_EXAM_DETAIL = gql`
  query($exam_id: Int!) {
    getExamResultDetail(exam_id: $exam_id) {
      id name className date appeared avgPct highPct lowPct passPct totalQuestions
      maxMarks marksPerCorrect marksPerWrong avgMarks highMarks lowMarks
      sections
      subjectNames
      subjectAvg { subject pct avgMarks maxMarks }
      students {
        rank name mobile sectionName studentUserId correct wrong totalQuestions pct percentile
        marks correctMarks negativeMarks maxMarks
        subjects { subject correct wrong total pct marks maxMarks }
      }
    }
  }
`;

// Question-wise Correct / Wrong / Unattempted. `section` follows the drill-down's section
// filter, so this tab describes the same cohort as the Students tab.
const GET_QUESTION_ANALYSIS = gql`
  query($exam_id: Int!, $section: String) {
    getExamQuestionAnalysis(exam_id: $exam_id, section: $section) {
      takers totalQuestions
      totalCorrect totalWrong totalUnattempted
      correctPct wrongPct unattemptedPct errorPct
      okQuestions warnQuestions criticalQuestions
      questions {
        slno questionId subject question questionText takers
        correct wrong unattempted
        correctPct wrongPct unattemptedPct errorPct
        option1 option2 option3 option4 answer explanation
        option1Count option2Count option3Count option4Count
        options { optionIndex optionKey optionText count pct isCorrect }
      }
    }
  }
`;

// College branding for the report card (logo / name / address).
const GET_ORG_PROFILE = gql`
  query($institution_id: Int!) {
    getOrganisationProfile(institution_id: $institution_id) {
      id
      organisation_name
      logo
      complete_address
    }
  }
`;

// Subject-wise batch comparison, keyed by (section within a branch), aggregated across
// every matching exam. We roll the sections up to branch level on the client. `avg_accuracy`
// is a percentage, so it lives on the SAME accuracy scale as the rest of this tab.
const GET_BATCH_SUBJECT_COMPARISON = gql`
  query($institution_id: Int!, $branch_ids: [Int], $section_ids: [Int], $class_ids: [Int]) {
    getBatchSubjectComparison(institution_id: $institution_id, branch_ids: $branch_ids, section_ids: $section_ids, class_ids: $class_ids) {
      section_id section_name branch_id branch_name total_students avg_score
      subjects { subject_id subject avg_score avg_accuracy students }
    }
  }
`;

// getOrganisationProfile returns the logo as a bare filename ("1782126069892.png"), which
// is served from the files host — the same base the existing report-card modal uses. Also
// tolerate a full URL or base64, in case the value is ever stored that way.
const LOGO_BASE = "https://support.rizee.in/files/";
const normalizeLogoSrc = (v) => {
  if (!v) return "";
  const s = String(v).trim();
  if (!s || s === "null" || s === "undefined") return "";
  if (s.startsWith("data:") || /^https?:\/\//i.test(s) || s.startsWith("//")) return s;
  if (/^\/9j\//.test(s)) return `data:image/jpeg;base64,${s}`;
  if (/^iVBORw0KGgo/.test(s)) return `data:image/png;base64,${s}`;
  if (/^R0lGOD/.test(s)) return `data:image/gif;base64,${s}`;
  if (/^[A-Za-z0-9+/]{200,}={0,2}$/.test(s)) return `data:image/jpeg;base64,${s}`;
  return LOGO_BASE + s.replace(/^\/+/, "");   // bare filename
};

// Error rate = share of the batch that ANSWERED a question WRONG. Blanks are excluded:
// a skipped question is a different failure from one attempted and got wrong.
// Banded as agreed: under 50 is fine, 50-70 warns (orange), above 70 is critical (red).
const errBand = (pct) => {
  const n = Number(pct) || 0;
  if (n > 70) return { color: "#dc2626", bg: "#fef2f2", label: "Critical" };   // red
  if (n >= 50) return { color: "#ea580c", bg: "#fff7ed", label: "High" };      // orange
  return { color: "#16a34a", bg: "#f0fdf4", label: "OK" };                     // green
};
// Error % rendered as a coloured pill — used in the exam list and the question table.
const ErrPill = ({ pct }) => {
  const b = errBand(pct);
  return (
    <span title={`${b.label} — ${pct}% answered this wrong`}
      style={{ display: "inline-block", minWidth: 46, textAlign: "center", background: b.bg, color: b.color, border: `1px solid ${b.color}33`, borderRadius: 999, padding: "2px 8px", fontSize: 12, fontWeight: 800 }}>
      {pct}%
    </span>
  );
};

// Same pill, but `good` flips the colour scale — for Correct, a high number is a good thing.
const RatePill = ({ pct, good }) => {
  const n = Number(pct) || 0;
  const b = good
    ? (n >= 50 ? { color: "#16a34a", bg: "#f0fdf4" } : n >= 30 ? { color: "#ea580c", bg: "#fff7ed" } : { color: "#dc2626", bg: "#fef2f2" })
    : errBand(n);
  return (
    <span style={{ display: "inline-block", minWidth: 46, textAlign: "center", background: b.bg, color: b.color, border: `1px solid ${b.color}33`, borderRadius: 999, padding: "2px 8px", fontSize: 12, fontWeight: 800 }}>
      {n}%
    </span>
  );
};

// Bands for the Question Analysis subject grid — same cut-points as Score Distribution,
// applied to whichever outcome you pick in the metric dropdown.
const QBAND_CUTS = [
  { label: "80-100%", min: 80, max: 101 },
  { label: "60-79%", min: 60, max: 80 },
  { label: "40-59%", min: 40, max: 60 },
  { label: "20-39%", min: 20, max: 40 },
  { label: "0-19%", min: 0, max: 20 },
];
// The three outcomes a question can have. `good: true` means a HIGH percentage is a good
// thing (Correct), so the colour ramp runs the other way than it does for Wrong / Blank —
// otherwise "80-100% answered it correctly" would render in alarming red.
const QA_METRICS = {
  wrong: { key: "wrongPct", label: "Wrong", verb: "answered them wrong", good: false },
  correct: { key: "correctPct", label: "Correct", verb: "answered them correctly", good: true },
  unattempted: { key: "unattemptedPct", label: "Unattempted", verb: "left them blank", good: false },
};
const RAMP_BAD = ["#dc2626", "#fb923c", "#f59e0b", "#22c55e", "#16a34a"];   // high = bad
const RAMP_GOOD = ["#16a34a", "#22c55e", "#f59e0b", "#fb923c", "#dc2626"];  // high = good
const qBands = (good) =>
  QBAND_CUTS.map((b, i) => ({ ...b, color: (good ? RAMP_GOOD : RAMP_BAD)[i] }));

// Compress question numbers into ranges: [1,2,3,7,8,10] -> "1-3, 7-8, 10". A band cell can
// hold 70+ questions, and listing every number is unreadable.
const numRanges = (nums) => {
  const xs = [...new Set(nums.filter((n) => Number.isFinite(n)))].sort((a, b) => a - b);
  const out = [];
  let i = 0;
  while (i < xs.length) {
    let j = i;
    while (j + 1 < xs.length && xs[j + 1] === xs[j] + 1) j += 1;
    out.push(i === j ? `${xs[i]}` : `${xs[i]}-${xs[j]}`);
    i = j + 1;
  }
  return out.join(", ");
};

const toCsv = (arr) => (arr && arr.length ? arr.join(",") : null);
const inSel = (arr, val) => !arr || !arr.length || arr.some((x) => String(x) === String(val));
// Effective branch ids = scope branches matching selected state/city, intersected
// with the explicit branch selection (so state/city always narrow the result).
const effectiveBranchCsv = (scope, sel) => toCsv(effectiveBranches(scope, sel).map((b) => b.id));

// The branches actually in play — scope branches matching the selected state/city,
// intersected with the explicit branch selection (empty selection = all scoped branches).
// Returns {id, name} so the branch-comparison view has labels to render.
function effectiveBranches(scope, sel) {
  const s = sel || {};
  // Branch is chosen ONCE in the header (NavbarOne) and applies to every tab; State/City
  // only narrow within that selection. See utils/branchScope + misScope.headerBranches.
  const scoped = headerBranches(scope).filter((b) => inSel(s.state, b.state_id) && inSel(s.city, b.city_id));
  return scoped.map((b) => ({ id: b.id, name: b.branch_name || `Branch ${b.id}` }));
}

// Roll a set of per-exam rows up into one summary. Avg / Pass / Error are weighted by
// `appeared` (a 200-student exam should outweigh a 12-student one); High / Low take the
// true max / min across exams that were actually sat. Mirrors the All-Exams footer math.
const aggregateExams = (rows) => {
  const list = rows || [];
  const students = list.reduce((a, r) => a + (r.totalStudents || 0), 0);
  const appeared = list.reduce((a, r) => a + (r.appeared || 0), 0);
  const wmean = (k) => (appeared ? Math.round(list.reduce((a, r) => a + (r[k] || 0) * (r.appeared || 0), 0) / appeared) : 0);
  const taken = list.filter((r) => (r.appeared || 0) > 0);
  return {
    exams: list.length,
    students, appeared,
    avg: wmean("avgPct"), pass: wmean("passPct"), error: wmean("errorPct"),
    high: taken.length ? Math.max(...taken.map((r) => r.highPct || 0)) : 0,
    low: taken.length ? Math.min(...taken.map((r) => r.lowPct || 0)) : 0,
    turnout: students ? Math.round((appeared / students) * 100) : 0,
  };
};

// Score bands used by the overall distribution and the branch heatmap. High = good, so the
// colour ramp runs green (top) to red (bottom).
const SCORE_BANDS = [
  { label: "80–100%", min: 80, max: 101, color: "#16a34a" },
  { label: "60–79%", min: 60, max: 80, color: "#22c55e" },
  { label: "40–59%", min: 40, max: 60, color: "#f59e0b" },
  { label: "20–39%", min: 20, max: 40, color: "#fb923c" },
  { label: "0–19%", min: 0, max: 20, color: "#dc2626" },
];
const bandColor = (pct) => (SCORE_BANDS.find((b) => pct >= b.min && pct < b.max) || SCORE_BANDS[SCORE_BANDS.length - 1]).color;

const S = {
  page: { background: "linear-gradient(135deg, #f0f4ff 0%, #faf5ff 100%)", padding: "20px 24px 60px", minHeight: "100%" },
  h1: { fontSize: 22, fontWeight: 800, color: "#1f1b2e", margin: 0, letterSpacing: "-0.02em" },
  sub: { fontSize: 13, color: "#726f84", marginTop: 3 },
  card: { background: "#fff", borderRadius: 16, border: "1px solid #e9e6f4", boxShadow: "0 1px 2px rgba(24,16,48,0.04), 0 8px 24px -14px rgba(88,52,160,0.16)" },
  cardPad: { padding: "18px 20px" },
  cardTitle: { fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 12 },
  filters: { display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap", padding: "14px 16px", marginBottom: 16 },
  kpiGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(158px, 1fr))", gap: 12, marginBottom: 16 },
  kpiCard: { position: "relative", padding: "14px 16px 15px", overflow: "hidden" },
  kpiLabel: { fontSize: 10.5, color: "#726f84", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" },
  kpiValue: { fontSize: 25, fontWeight: 820, color: "#1f1b2e", marginTop: 5, letterSpacing: "-0.03em", lineHeight: 1, fontVariantNumeric: "tabular-nums" },
  th: { textAlign: "left", fontSize: 11, fontWeight: 700, color: "#726f84", textTransform: "uppercase", padding: "9px 10px", borderBottom: "1.5px solid #ddd8ec", whiteSpace: "nowrap" },
  td: { fontSize: 13, color: "#334155", padding: "9px 10px", borderBottom: "1px solid #f1eefb", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" },
  empty: { padding: "32px 10px", textAlign: "center", color: "#a29fb4", fontSize: 13 },
  loadingOverlay: { position: "absolute", inset: 0, background: "rgba(255,255,255,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 5, borderRadius: 16 },
  skeleton: { height: 16, borderRadius: 6, background: "linear-gradient(90deg,#eef2f7 25%,#f8fafc 37%,#eef2f7 63%)", margin: "4px 0" },
  // icon-chip card header (finance look): chip + title + subtitle, optional right-aligned control
  cardHead: { display: "flex", alignItems: "center", gap: 12, padding: "16px 20px", borderBottom: "1px solid #e9e6f4" },
  chip: { width: 34, height: 34, borderRadius: 10, flex: "none", display: "grid", placeItems: "center", background: "#f3f0ff", color: "#7c3aed", fontSize: 15 },
  headTitle: { fontSize: 15, fontWeight: 750, color: "#1f1b2e", letterSpacing: "-0.01em" },
  headSub: { fontSize: 12, color: "#726f84", marginTop: 1 },
};
// Reusable icon-chip section header — the finance-module look, shared by every new card.
const CardHead = ({ icon, title, sub, right }) => (
  <div style={S.cardHead}>
    <div style={S.chip}><i className={icon} /></div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={S.headTitle}>{title}</div>
      {sub ? <div style={S.headSub}>{sub}</div> : null}
    </div>
    {right || null}
  </div>
);
const Spinner = () => <i className="fal fa-spinner fa-spin" style={{ fontSize: 26, color: "#7c3aed" }} />;

const fmtDate = (epoch) => {
  if (!epoch) return "—";
  let n = parseInt(epoch);
  if (!n) return "—";
  if (n < 1e12) n *= 1000; // seconds -> ms
  const d = new Date(n);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
};

// Marks are Floats (schemes can be +4 / -1, but also 0.5 or 0.25), so trim the trailing
// ".0" rather than printing "235.00" everywhere.
const fmtMarks = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
};

// Date + time (e.g. "24 Jun 2026, 10:30 AM")
const fmtDateTime = (epoch) => {
  if (!epoch) return "—";
  let n = parseInt(epoch);
  if (!n) return "—";
  if (n < 1e12) n *= 1000;
  const d = new Date(n);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) +
    ", " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
};

class ExamReportsTab extends Component {
  state = {
    view: "reports", sel: { state: [], city: [], branch: [], class: [], stream: [], section: [] }, rows: [], loading: true, detail: null, detailLoading: false, trendMode: "week", examSort: { sortKey: "", sortDir: "" }, detailSort: { sortKey: "", sortDir: "" }, examPage: 1, examPageSize: 10, examSearch: "", org: null, detailSection: "ALL", detailTab: "students", qa: null, qaLoading: false, qaError: "", qaSubject: "ALL", qaMetric: "wrong", qaSort: { sortKey: "", sortDir: "" },
    branchAgg: [], branchLoading: false, branchSort: { sortKey: "avg", sortDir: "desc" }, branchMetric: "avg",
    subjRows: [], subjLoading: false
  };

  setBranchSort = (key) => this.setState((s) => ({ branchSort: nextSort(s.branchSort, key) }));

  // Sorting re-orders the whole set, so jump back to page 1 (otherwise you'd land
  // mid-way through a list that no longer means the same thing).
  setExamSort = (key) => this.setState((s) => ({ examSort: nextSort(s.examSort, key), examPage: 1 }));
  setExamPage = (p) => this.setState({ examPage: p });
  setExamPageSize = (n) => this.setState({ examPageSize: n, examPage: 1 });
  // A new search narrows the set, so the current page number is no longer meaningful.
  setExamSearch = (v) => this.setState({ examSearch: v, examPage: 1 });
  setDetailSort = (key) => this.setState((s) => ({ detailSort: nextSort(s.detailSort, key) }));

  componentDidMount() { this.defaultSel(); this.load(); this.loadOrg(); }
  componentDidUpdate(prevProps) {
    if (prevProps.scope !== this.props.scope) this.defaultSel();
  }

  // College branding for the report card header. Non-blocking: if it fails the card
  // still prints, just falling back to the institution name from cookies.
  loadOrg = async () => {
    const instId = parseInt(Cookies.get("institutionid")) || 118;
    try {
      const res = await this.props.client.query({
        query: GET_ORG_PROFILE,
        variables: { institution_id: instId },
      });
      this.setState({ org: (res.data && res.data.getOrganisationProfile) || null });
    } catch (e) { console.error("org profile", e); }
  };

  defaultSel = () => {
    if (this._defaulted) return;
    const sc = this.props.scope || {};
    if (!((sc.branches || []).length || (sc.classes || []).length || (sc.sections || []).length)) return;
    this._defaulted = true;
    this.setState({
      sel: {
        state: (sc.states || []).map((s) => s.state_id),
        city: (sc.cities || []).map((c) => c.id),
        branch: (sc.branches || []).map((b) => b.id),
        class: [...new Set((sc.classes || []).map((c) => c.id))],
        stream: [...new Set((sc.streams || []).map((s) => s.id))],
        section: (sc.sections || []).map((s) => s.id),
      },
    }, this.load);
  };

  setSel = (nextSel) => this.setState({ sel: nextSel }, this.load);

  load = async () => {
    const instId = parseInt(Cookies.get("institutionid")) || 118;
    const { sel } = this.state;
    this.setState({ loading: true });
    try {
      const res = await this.props.client.query({
        query: GET_EXAM_REPORTS,
        variables: { institute_id: instId, branch_id: effectiveBranchCsv(this.props.scope || {}, sel), class_id: toCsv(sel.class), section_id: toCsv(sel.section), stream_id: toCsv(sel.stream) },
        fetchPolicy: "network-only",
      });
      // New scope → new exam list, so start back at page 1.
      this.setState({ rows: (res.data && res.data.getExamReports) || [], loading: false, examPage: 1 });
    } catch (e) { console.error("exam reports", e); this.setState({ rows: [], loading: false }); }
    // Fire the two branch-level views off the same selection. They own their own
    // loading flags so the main table never waits on them.
    this.loadBranchCompare();
    this.loadSubjectByBranch();
  };

  // Branch-wise comparison. Each exam paper belongs to one branch, so fetching the exam
  // list once per branch (in parallel) cleanly partitions the exams and — critically —
  // keeps the SAME accuracy-% metric as the rest of the tab. One extra query per branch.
  loadBranchCompare = async () => {
    const instId = parseInt(Cookies.get("institutionid")) || 118;
    const { sel } = this.state;
    const branches = effectiveBranches(this.props.scope || {}, sel);
    // Nothing to compare with a single branch in scope.
    if (branches.length < 2) { this.setState({ branchAgg: [], branchLoading: false }); return; }
    this.setState({ branchLoading: true });
    try {
      const results = await Promise.all(branches.map((b) =>
        this.props.client.query({
          query: GET_EXAM_REPORTS,
          variables: { institute_id: instId, branch_id: String(b.id), class_id: toCsv(sel.class), section_id: toCsv(sel.section), stream_id: toCsv(sel.stream) },
          fetchPolicy: "network-only",
        }).then((res) => ({ branch: b, rows: (res.data && res.data.getExamReports) || [] }))
          .catch((e) => { console.error("branch exams", b.id, e); return { branch: b, rows: [] }; })
      ));
      const branchAgg = results
        .map(({ branch, rows }) => ({ id: branch.id, name: branch.name, ...aggregateExams(rows) }))
        .filter((r) => r.exams > 0);
      this.setState({ branchAgg, branchLoading: false });
    } catch (e) { console.error("branch compare", e); this.setState({ branchAgg: [], branchLoading: false }); }
  };

  // Subject-per-branch matrix. getBatchSubjectComparison returns one row per section; we
  // roll the sections up to their branch, averaging each subject's accuracy weighted by
  // the students behind it. Stays on the accuracy-% scale via `avg_accuracy`.
  loadSubjectByBranch = async () => {
    const instId = parseInt(Cookies.get("institutionid")) || 118;
    const { sel } = this.state;
    const branchIds = effectiveBranches(this.props.scope || {}, sel).map((b) => parseInt(b.id)).filter(Boolean);
    const toInts = (a) => (a || []).map((x) => parseInt(x)).filter(Boolean);
    this.setState({ subjLoading: true });
    try {
      const res = await this.props.client.query({
        query: GET_BATCH_SUBJECT_COMPARISON,
        variables: {
          institution_id: instId,
          branch_ids: branchIds.length ? branchIds : null,
          section_ids: toInts(sel.section).length ? toInts(sel.section) : null,
          class_ids: toInts(sel.class).length ? toInts(sel.class) : null,
        },
        fetchPolicy: "network-only",
      });
      this.setState({ subjRows: (res.data && res.data.getBatchSubjectComparison) || [], subjLoading: false });
    } catch (e) {
      // Older backend schema reads as empty — keep it non-fatal, the section just hides.
      console.error("subject by branch", e);
      this.setState({ subjRows: [], subjLoading: false });
    }
  };

  // Collapse the section-level subject rows into a per-branch subject matrix. Each subject's
  // accuracy is averaged across the branch's sections, weighted by the students behind each.
  buildSubjectMatrix = () => {
    const rows = this.state.subjRows || [];
    const byBranch = {};
    const subjectSet = new Map();     // subject_id -> name (union across branches)
    rows.forEach((sec) => {
      const bid = sec.branch_id;
      if (!byBranch[bid]) byBranch[bid] = { id: bid, name: sec.branch_name || `Branch ${bid}`, subs: {} };
      (sec.subjects || []).forEach((s) => {
        subjectSet.set(s.subject_id, s.subject || `Subject ${s.subject_id}`);
        const w = Number(s.students) || 0;
        const acc = Number(s.avg_accuracy) || 0;
        const cur = byBranch[bid].subs[s.subject_id] || { sum: 0, n: 0 };
        cur.sum += acc * w; cur.n += w;
        byBranch[bid].subs[s.subject_id] = cur;
      });
    });
    const subjects = [...subjectSet.entries()].map(([id, name]) => ({ id, name }));
    const branches = Object.values(byBranch).map((b) => {
      const cells = {};
      let sum = 0, n = 0;
      subjects.forEach((sub) => {
        const c = b.subs[sub.id];
        const v = c && c.n ? Math.round(c.sum / c.n) : null;
        cells[sub.id] = v;
        if (v != null) { sum += v; n += 1; }
      });
      return { id: b.id, name: b.name, cells, avg: n ? Math.round(sum / n) : null };
    }).sort((a, b) => (b.avg || 0) - (a.avg || 0));
    return { subjects, branches };
  };

  exportTables = () => {
    const rows = this.state.rows || [];
    return [{
      heading: `Exam Reports (${rows.length})`,
      columns: ["Exam", "Class", "Date", "Students", "Appeared", "Avg %", "High %", "Low %", "Error %", "Questions"],
      rows: rows.map((r) => [r.name, r.className, fmtDate(r.date), r.totalStudents, r.appeared, `${r.avgPct}%`, `${r.highPct}%`, `${r.lowPct}%`, `${r.errorPct}%`, r.totalQuestions]),
    }];
  };

  handleExcel = () => exportExcel(this.exportTables().map((x) => ({ name: "Exam Reports", aoa: [x.columns, ...x.rows] })), "exam_reports.xlsx");
  handlePdf = () => exportPdf("Exam Reports", this.exportTables(), "exam_reports.pdf");

  openExam = async (examId) => {
    // Reset the section filter and the tab — both are per-exam, and the previous exam's
    // sections may not even exist in this one.
    this.setState({
      detail: { id: examId }, detailLoading: true, detailSection: "ALL",
      detailTab: "students", qa: null, qaLoading: false, qaError: "", qaSubject: "ALL", qaMetric: "wrong", qaSort: { sortKey: "", sortDir: "" },
    });
    try {
      const res = await this.props.client.query({
        query: GET_EXAM_DETAIL,
        variables: { exam_id: parseInt(examId) },
        fetchPolicy: "network-only",
      });
      this.setState({ detail: (res.data && res.data.getExamResultDetail) || null, detailLoading: false });
    } catch (e) { console.error("exam detail", e); this.setState({ detailLoading: false }); }
  };

  closeExam = () => this.setState({ detail: null, detailLoading: false, qa: null });

  // Question analysis is loaded lazily — only when the tab is actually opened — because
  // it's a second round-trip that most people looking at the Students tab never need.
  loadQuestionAnalysis = async () => {
    const { detail, detailSection } = this.state;
    if (!detail || !detail.id) return;
    this.setState({ qaLoading: true });
    try {
      const res = await this.props.client.query({
        query: GET_QUESTION_ANALYSIS,
        variables: { exam_id: parseInt(detail.id), section: detailSection || "ALL" },
        fetchPolicy: "network-only",
      });
      this.setState({ qa: (res.data && res.data.getExamQuestionAnalysis) || null, qaLoading: false, qaError: "" });
    } catch (e) {
      // Don't disguise a failure as "no data" — an out-of-date backend schema (the usual
      // cause: .graphql files don't hot-reload) reads identically to an empty exam otherwise.
      console.error("question analysis", e);
      this.setState({ qaLoading: false, qa: null, qaError: (e && e.message) || "Request failed" });
    }
  };

  setDetailTab = (tab) => {
    this.setState({ detailTab: tab }, () => {
      if (tab === "questions" && !this.state.qa && !this.state.qaLoading) this.loadQuestionAnalysis();
    });
  };

  // The section filter drives BOTH tabs. Students re-derive client-side (the whole cohort
  // is already loaded); question analysis has to go back to the server, but only if that
  // tab is open — otherwise it's fetched fresh when you switch to it.
  setDetailSection = (sec) => {
    this.setState({ detailSection: sec, qa: null, qaSubject: "ALL" }, () => {
      if (this.state.detailTab === "questions") this.loadQuestionAnalysis();
    });
  };

  setQaSort = (key) => this.setState((s) => ({ qaSort: nextSort(s.qaSort, key) }));

  // ── Report card ─────────────────────────────────────────────────────────────
  // Builds the card as real HTML and prints it through an iframe. Deliberately NOT a
  // html2canvas raster — that mis-renders fixed layouts (it mangled the ID card), and
  // printing the markup gives crisp text and a correct A4 page.
  buildReportCardHTML = (s, d) => {
    const esc = (v) => String(v == null ? "" : v).replace(/[&<>"]/g, (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
    // Prefer the organisation profile (it carries the logo + address); fall back to the
    // institution name in cookies if the profile hasn't loaded / isn't set up.
    const org = this.state.org || {};
    const school = org.organisation_name || Cookies.get("institutionname")
      || Cookies.get("institute_name") || Cookies.get("institutename") || "Institution";
    const logo = normalizeLogoSrc(org.logo);
    const address = (org.complete_address || "").trim();
    const scored = Number(s.marks) || 0;
    const outOf = Number(s.maxMarks) || 0;
    const overallPct = outOf ? Math.round((scored / outOf) * 100) : 0;

    const subjectRows = (s.subjects || []).filter((x) => x.total > 0).map((x) => `
      <tr>
        <td class="l">${esc(x.subject)}</td>
        <td>${x.total}</td>
        <td class="ok">${x.correct}</td>
        <td class="bad">${x.wrong}</td>
        <td>${x.total - x.correct - x.wrong}</td>
        <td class="b">${fmtMarks(x.marks)} <span class="mut">/ ${fmtMarks(x.maxMarks)}</span></td>
        <td>${x.pct}%</td>
      </tr>`).join("");

    const stat = (label, value) =>
      `<div class="stat"><div class="sl">${esc(label)}</div><div class="sv">${esc(value)}</div></div>`;

    return `
      <div class="card">
        <div class="hd">
          ${logo ? `<img class="logo" src="${esc(logo)}" alt="" />` : ""}
          <div class="hdtext">
            <div class="school">${esc(school)}</div>
            ${address ? `<div class="addr">${esc(address)}</div>` : ""}
            <div class="title">Exam Report Card</div>
          </div>
        </div>

        <div class="meta">
          <div><span class="k">Student</span><span class="v">${esc(s.name)}</span></div>
          <div><span class="k">Mobile</span><span class="v">${esc(s.mobile)}</span></div>
          <div><span class="k">Exam</span><span class="v">${esc(d.name)}</span></div>
          <div><span class="k">Class</span><span class="v">${esc(d.className || "—")}</span></div>
          <div><span class="k">Date</span><span class="v">${esc(fmtDate(d.date))}</span></div>
          <div><span class="k">Appeared</span><span class="v">${d.appeared} students</span></div>
        </div>

        <table class="tbl">
          <thead>
            <tr>
              <th class="l">Subject</th><th>Questions</th><th>Correct</th><th>Wrong</th>
              <th>Unattempted</th><th>Marks</th><th>%</th>
            </tr>
          </thead>
          <tbody>
            ${subjectRows || `<tr><td colspan="7" class="mut c">No subject-wise data for this exam.</td></tr>`}
          </tbody>
          <tfoot>
            <tr>
              <td class="l">Total</td>
              <td>${s.totalQuestions}</td>
              <td class="ok">${s.correct}</td>
              <td class="bad">${s.wrong}</td>
              <td>${Math.max(0, (s.totalQuestions || 0) - (s.correct || 0) - (s.wrong || 0))}</td>
              <td class="b">${fmtMarks(scored)} <span class="mut">/ ${fmtMarks(outOf)}</span></td>
              <td>${overallPct}%</td>
            </tr>
          </tfoot>
        </table>

        <div class="stats">
          ${stat("Marks", `${fmtMarks(scored)} / ${fmtMarks(outOf)}`)}
          ${stat("Percentage", `${overallPct}%`)}
          ${stat("Rank", `${s.rank} of ${d.appeared}`)}
          ${stat("Percentile", String(s.percentile))}
        </div>

        ${d.marksPerCorrect ? `<div class="note">Marking scheme: +${fmtMarks(d.marksPerCorrect)} per correct${d.marksPerWrong ? `, −${fmtMarks(d.marksPerWrong)} per wrong` : ""}. Percentile = % of the ${d.appeared} students who scored below this student.</div>` : ""}
      </div>`;
  };

  // Shared print CSS for the report card + results sheet.
  static PRINT_CSS = `
    @page { size: A4; margin: 12mm; }
    *{box-sizing:border-box;-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important}
    body{margin:0;font-family:Arial,Helvetica,sans-serif;color:#0f172a}
    .card{border:1px solid #e2e8f0;border-radius:10px;padding:22px}
    /* One card per sheet when printing a batch; the last must not add a blank page. */
    .card + .card{page-break-before:always}
    .hd{display:flex;align-items:center;gap:16px;border-bottom:2px solid #7c3aed;padding-bottom:12px;margin-bottom:16px}
    .logo{width:64px;height:64px;object-fit:contain;flex-shrink:0}
    .hdtext{flex:1;min-width:0;text-align:center}
    .school{font-size:20px;font-weight:800;text-transform:uppercase;letter-spacing:.5px}
    .addr{font-size:10.5px;color:#64748b;margin-top:3px;line-height:1.4}
    .title{font-size:13px;font-weight:700;color:#7c3aed;text-transform:uppercase;letter-spacing:1.5px;margin-top:5px}
    .meta{display:grid;grid-template-columns:repeat(3,1fr);gap:8px 18px;margin-bottom:18px}
    .meta .k{display:block;font-size:10px;color:#64748b;font-weight:700;text-transform:uppercase;letter-spacing:.4px}
    .meta .v{display:block;font-size:13px;font-weight:600;margin-top:1px}
    .tbl{width:100%;border-collapse:collapse;margin-bottom:18px}
    .tbl th{font-size:10px;text-transform:uppercase;letter-spacing:.4px;color:#64748b;background:#f8fafc;
            border-top:1px solid #e2e8f0;border-bottom:2px solid #e2e8f0;padding:7px 8px;text-align:center}
    .tbl td{font-size:12px;padding:6px 8px;border-bottom:1px solid #f1f5f9;text-align:center}
    .tbl tfoot td{border-top:2px solid #e2e8f0;border-bottom:none;background:#f8fafc;font-weight:800}
    /* Repeat the header on every page of a long results table. */
    .tbl thead{display:table-header-group}
    .tbl tr{page-break-inside:avoid}
    .l{text-align:left!important} .c{text-align:center}
    .b{font-weight:800} .mut{color:#94a3b8;font-weight:500}
    .ok{color:#16a34a;font-weight:700} .bad{color:#dc2626}
    .stats{display:grid;grid-template-columns:repeat(4,1fr);gap:10px}
    .stat{border:1px solid #e2e8f0;border-radius:8px;padding:10px 12px;text-align:center}
    .sl{font-size:10px;color:#64748b;font-weight:700;text-transform:uppercase;letter-spacing:.4px}
    .sv{font-size:17px;font-weight:800;color:#7c3aed;margin-top:2px}
    .note{margin-top:14px;font-size:10.5px;color:#94a3b8;line-height:1.5}
  `;

  // Print arbitrary HTML through a hidden iframe. Deliberately NOT html2canvas — that
  // rasteriser mangles fixed layouts; printing real markup gives crisp text and a real
  // A4 page. Waits for images (the logo) since print() doesn't wait for them itself.
  printHTML = (title, bodyHTML) => {
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
    document.body.appendChild(iframe);
    const cleanup = () => setTimeout(() => { try { document.body.removeChild(iframe); } catch (e) { } }, 1000);

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(`<!DOCTYPE html><html><head><title>${title}</title>
      <style>${ExamReportsTab.PRINT_CSS}</style></head><body>${bodyHTML}</body></html>`);
    doc.close();

    const win = iframe.contentWindow;
    const imgs = Array.from(doc.images || []);
    Promise.all(imgs.map((img) => (img.complete
      ? Promise.resolve()
      : new Promise((res) => { img.onload = res; img.onerror = res; }))))
      .then(() => setTimeout(() => {
        try { win.focus(); win.print(); } catch (e) { }
        cleanup();
      }, 200));
  };

  // `view` is the section-filtered exam (or the raw exam when no section is picked), so a
  // card printed while a section is selected quotes that section's rank / cohort size —
  // matching what the row on screen says.
  printReportCard = (s, view) => {
    const d = view || this.state.detail;
    if (!d || !s) return;
    this.printHTML(`Report Card — ${s.name}`, this.buildReportCardHTML(s, d));
  };

  // The whole exam's results as one sheet: rank, student, subject-wise marks, total,
  // percentile — the same data as the on-screen table.
  printResults = () => {
    const d = this.state.detail;
    if (!d) return;
    // Print exactly what's on screen: the sheet follows the Section filter, and the
    // header figures are recomputed for that same cohort.
    const sec = this.state.detailSection || "ALL";
    const students = (d.students || []).filter((s) => sec === "ALL" || (s.sectionName || "") === sec);
    if (!students.length) return;
    const v = sec === "ALL" ? d : this.deriveView(d, students);

    const esc = (v2) => String(v2 == null ? "" : v2).replace(/[&<>"]/g, (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
    const org = this.state.org || {};
    const school = org.organisation_name || Cookies.get("institutionname")
      || Cookies.get("institute_name") || Cookies.get("institutename") || "Institution";
    const logo = normalizeLogoSrc(org.logo);
    const subs = d.subjectNames || [];

    const head = `
      <tr>
        <th>Rank</th><th class="l">Student</th><th>Mobile</th><th>Section</th>
        ${subs.map((x) => `<th>${esc(x)}</th>`).join("")}
        <th>Marks</th><th>Correct</th><th>Wrong</th><th>%</th><th>Percentile</th>
      </tr>`;

    const body = students.map((s) => `
      <tr>
        <td class="b">${s.rank}</td>
        <td class="l">${esc(s.name)}</td>
        <td>${esc(s.mobile)}</td>
        <td>${esc(s.sectionName || "—")}</td>
        ${subs.map((x) => {
      const m = (s.subjects || []).find((y) => y.subject === x);
      return `<td>${m && m.total ? `${fmtMarks(m.marks)} <span class="mut">/${fmtMarks(m.maxMarks)}</span>` : "—"}</td>`;
    }).join("")}
        <td class="b">${fmtMarks(s.marks)} <span class="mut">/${fmtMarks(s.maxMarks)}</span></td>
        <td class="ok">${s.correct}</td>
        <td class="bad">${s.wrong}</td>
        <td>${s.pct}%</td>
        <td class="b">${s.percentile}</td>
      </tr>`).join("");

    this.printHTML(`Exam Results — ${d.name}`, `
      <div class="card">
        <div class="hd">
          ${logo ? `<img class="logo" src="${esc(logo)}" alt="" />` : ""}
          <div class="hdtext">
            <div class="school">${esc(school)}</div>
            <div class="title">Exam Results${sec === "ALL" ? "" : ` — Section ${esc(sec)}`}</div>
          </div>
        </div>
        <div class="meta">
          <div><span class="k">Exam</span><span class="v">${esc(d.name)}</span></div>
          <div><span class="k">Class</span><span class="v">${esc(d.className || "—")}</span></div>
          <div><span class="k">Section</span><span class="v">${sec === "ALL" ? "All" : esc(sec)}</span></div>
          <div><span class="k">Date</span><span class="v">${esc(fmtDate(d.date))}</span></div>
          <div><span class="k">Appeared</span><span class="v">${v.appeared}</span></div>
          <div><span class="k">Max Marks</span><span class="v">${fmtMarks(v.maxMarks)}</span></div>
          <div><span class="k">Avg Marks</span><span class="v">${fmtMarks(v.avgMarks)}</span></div>
        </div>
        <table class="tbl"><thead>${head}</thead><tbody>${body}</tbody></table>
      </div>`);
  };

  // Pager for the All-Exams table: page-size picker + Prev / numbered pages / Next.
  renderExamPager = (total, page, pages, start, shown) => {
    if (!total) return null;
    const btn = (label, target, disabled, active) => (
      <button key={label} disabled={disabled} onClick={() => this.setExamPage(target)}
        style={{
          border: `1px solid ${active ? "#7c3aed" : "#e5e7eb"}`,
          background: active ? "#7c3aed" : "#fff",
          color: active ? "#fff" : (disabled ? "#cbd5e1" : "#334155"),
          borderRadius: 8, padding: "5px 11px", fontSize: 13, fontWeight: 600,
          cursor: disabled ? "default" : "pointer", margin: "0 3px",
        }}>{label}</button>
    );
    // Show a sliding window of up to 5 page numbers around the current page.
    const nums = [];
    const from = Math.max(1, Math.min(page - 2, pages - 4));
    for (let p = from; p <= Math.min(pages, from + 4); p++) nums.push(p);

    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginTop: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <select value={this.state.examPageSize} onChange={(e) => this.setExamPageSize(parseInt(e.target.value, 10))}
            style={{ border: "1.5px solid #e5e7eb", borderRadius: 8, padding: "5px 8px", fontSize: 13, color: "#334155", background: "#fff", cursor: "pointer" }}>
            {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <span style={{ fontSize: 12, color: "#94a3b8" }}>
            Showing {total ? start + 1 : 0} to {start + shown} of {total} exams
          </span>
        </div>
        {pages > 1 && (
          <div>
            {btn("Prev", page - 1, page <= 1)}
            {nums.map((p) => btn(String(p), p, false, p === page))}
            {btn("Next", page + 1, page >= pages)}
          </div>
        )}
      </div>
    );
  };

  // Navigate to the student's performance profile.
  openStudentProfile = (s) => {
    const student = {
      student_user_id: s.studentUserId ? parseInt(s.studentUserId) : null,
      student_name: s.name,
      contact_no: s.mobile,
      student_mobile: s.mobile,
    };
    this.props.history.push("/student-profile-1/overview", { student });
  };

  // ── Overall score-band distribution ─────────────────────────────────────────
  // How the exams themselves spread across average-score bands — the quick read on
  // whether the cohort clusters high, low, or splits.
  renderScoreDistribution = (rows) => {
    const list = rows || [];
    const bands = SCORE_BANDS.map((b) => ({
      ...b,
      count: list.filter((r) => (r.appeared || 0) > 0 && (r.avgPct || 0) >= b.min && (r.avgPct || 0) < b.max).length,
    }));
    const total = bands.reduce((a, b) => a + b.count, 0);
    const max = Math.max(1, ...bands.map((b) => b.count));
    return (
      <div style={{ ...S.card, marginBottom: 16 }}>
        <CardHead icon="fal fa-layer-group" title="Score Distribution" sub="Exams grouped by their average score" />
        <div style={S.cardPad}>
          {total === 0 ? (
            <div style={S.empty}>No exam data for this selection.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              {bands.map((b) => (
                <div key={b.label} style={{ display: "grid", gridTemplateColumns: "78px 1fr 118px", alignItems: "center", gap: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 750, color: b.color }}>{b.label}</div>
                  <div style={{ background: "#f1eefb", borderRadius: 7, height: 22, position: "relative", overflow: "hidden" }}>
                    <div style={{ width: `${(b.count / max) * 100}%`, background: b.color, height: "100%", borderRadius: 7, minWidth: b.count ? 4 : 0, transition: "width .3s" }} />
                  </div>
                  <div style={{ textAlign: "right", fontSize: 12, color: "#726f84" }}>
                    <b style={{ color: "#1f1b2e" }}>{b.count}</b> exam{b.count === 1 ? "" : "s"}
                    <span style={{ color: "#a29fb4" }}> · {total ? Math.round((b.count / total) * 100) : 0}%</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── Branch-wise comparison ──────────────────────────────────────────────────
  // Ranked table + bar chart over the per-branch aggregates. Rank always reflects Avg
  // Score, whatever column the table is sorted by. Only shown when 2+ branches are in scope.
  renderBranchCompare = () => {
    const { branchAgg, branchLoading, branchSort } = this.state;
    if (branchLoading) {
      return <div style={{ ...S.card, marginBottom: 16 }}>
        <CardHead icon="fal fa-code-branch" title="Branch-wise Comparison" sub="Loading…" />
        <div style={S.cardPad}><TableShimmer rows={4} cols={6} /></div>
      </div>;
    }
    if (!branchAgg || branchAgg.length < 2) return null;

    // Rank by Avg Score (best first), independent of the current table sort.
    const byAvg = [...branchAgg].sort((a, b) => b.avg - a.avg);
    const rankOf = {}; byAvg.forEach((r, i) => { rankOf[r.id] = i + 1; });
    const shown = branchSort.sortKey ? sortRows(branchAgg, branchSort.sortKey, branchSort.sortDir) : byAvg;

    const chartOptions = {
      chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
      plotOptions: { bar: { borderRadius: 5, columnWidth: "58%" } },
      colors: ["#7c3aed", "#16a34a"], dataLabels: { enabled: false },
      xaxis: { categories: byAvg.map((b) => b.name), labels: { rotate: -30, style: { fontSize: "11px", colors: "#726f84" }, trim: true, maxHeight: 70 } },
      yaxis: { min: 0, max: 100, title: { text: "%", style: { color: "#a29fb4" } }, labels: { style: { colors: "#a29fb4" } } },
      legend: { position: "top", fontSize: "12px", markers: { radius: 3 } }, grid: { borderColor: "#f1eefb" }, noData: { text: "No data" },
    };
    const chartSeries = [
      { name: "Avg Score", data: byAvg.map((b) => b.avg) },
    ];
    const th = { ...S.th };
    const pill = (v, color) => (
      <span style={{ display: "inline-block", minWidth: 46, padding: "3px 9px", borderRadius: 999, fontSize: 11.5, fontWeight: 800, color, background: `${color}17` }}>{v}%</span>
    );
    const cols = [
      { key: "exams", label: "Exams" }, { key: "appeared", label: "Appeared" },
      { key: "turnout", label: "Turnout %" }, { key: "avg", label: "Avg %" },
      { key: "high", label: "High %" },
      { key: "low", label: "Low %" }, { key: "error", label: "Error %" },
    ];

    return (
      <div style={{ ...S.card, marginBottom: 16 }}>
        <CardHead icon="fal fa-code-branch" title="Branch-wise Comparison" sub={`${branchAgg.length} branches · same accuracy metric as the summary above`} />
        <div style={S.cardPad}>
          <ReactApexChart options={chartOptions} series={chartSeries} type="bar" height={290} />
          <div style={{ overflowX: "auto", marginTop: 10 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
              <thead>
                <tr>
                  <th style={{ ...th, textAlign: "center" }}>#</th>
                  <SortTh label="Branch" sortKey="name" state={branchSort} onSort={this.setBranchSort} style={th} />
                  {cols.map((c) => (
                    <SortTh key={c.key} label={c.label} sortKey={c.key} state={branchSort} onSort={this.setBranchSort} style={th} center />
                  ))}
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => {
                  const rank = rankOf[r.id];
                  const badge = {
                    display: "inline-grid", placeItems: "center", width: 22, height: 22, borderRadius: 7, fontSize: 11, fontWeight: 800,
                    ...(rank === 1 ? { background: "linear-gradient(135deg,#7c3aed,#6d28d9)", color: "#fff" } : { background: "#f3f0ff", color: "#7c3aed" })
                  };
                  return (
                    <tr key={r.id}>
                      <td style={{ ...S.td, textAlign: "center" }}><span style={badge}>{rank}</span></td>
                      <td style={{ ...S.td, fontWeight: 700 }}>{r.name}</td>
                      <td style={{ ...S.td, textAlign: "center" }}>{r.exams}</td>
                      <td style={{ ...S.td, textAlign: "center" }}>{r.appeared.toLocaleString()}</td>
                      <td style={{ ...S.td, textAlign: "center" }}>{r.turnout}%</td>
                      <td style={{ ...S.td, textAlign: "center" }}>{pill(r.avg, bandColor(r.avg))}</td>
                      <td style={{ ...S.td, textAlign: "center", color: "#16a34a" }}>{r.high}%</td>
                      <td style={{ ...S.td, textAlign: "center", color: "#dc2626" }}>{r.low}%</td>
                      <td style={{ ...S.td, textAlign: "center" }}><ErrPill pct={r.error} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // ── Subject performance by branch ───────────────────────────────────────────
  // Branch × subject accuracy matrix, rolled up from the section-level batch comparison.
  // Cells coloured on the same score bands as the distribution above.
  renderSubjectMatrix = () => {
    const { subjLoading } = this.state;
    if (subjLoading) {
      return <div style={{ ...S.card, marginBottom: 16 }}>
        <CardHead icon="fal fa-th" title="Subject Performance by Branch" sub="Loading…" />
        <div style={S.cardPad}><TableShimmer rows={4} cols={6} /></div>
      </div>;
    }
    const { subjects, branches } = this.buildSubjectMatrix();
    if (!subjects.length || !branches.length) return null;

    const th = { ...S.th, textAlign: "center" };
    const cell = (v) => v == null
      ? <span style={{ color: "#cbd5e1" }}>—</span>
      : <span style={{ display: "inline-block", minWidth: 46, padding: "4px 9px", borderRadius: 8, fontWeight: 750, fontSize: 12, color: bandColor(v), background: `${bandColor(v)}17` }}>{v}%</span>;

    return (
      <div style={{ ...S.card, marginBottom: 16 }}>
        <CardHead icon="fal fa-th" title="Subject Performance by Branch" sub="Average accuracy per subject · best branch first" />
        <div style={S.cardPad}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 620 }}>
              <thead>
                <tr>
                  <th style={{ ...S.th }}>Branch</th>
                  <th style={th}>Overall</th>
                  {subjects.map((s) => <th key={s.id} style={th}>{s.name}</th>)}
                </tr>
              </thead>
              <tbody>
                {branches.map((b) => (
                  <tr key={b.id}>
                    <td style={{ ...S.td, fontWeight: 700 }}>{b.name}</td>
                    <td style={{ ...S.td, textAlign: "center", fontWeight: 800 }}>{cell(b.avg)}</td>
                    {subjects.map((s) => <td key={s.id} style={{ ...S.td, textAlign: "center" }}>{cell(b.cells[s.id])}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  render() {
    const { rows, loading } = this.state;
    const totalExams = rows.length;
    // Whole-selection summary — drives the KPI cards and the score-band distribution.
    // (The All-Exams footer uses its own search-narrowed aggregate below.)
    const overall = aggregateExams(rows);

    // Week-wise performance — aggregate ALL exams by week (chronological).
    const weekKey = (epoch) => {
      let n = parseInt(epoch); if (!n) return null; if (n < 1e12) n *= 1000;
      const d = new Date(n);
      const onejan = new Date(d.getFullYear(), 0, 1);
      const wk = Math.ceil(((d - onejan) / 86400000 + onejan.getDay() + 1) / 7);
      return { key: `${d.getFullYear()}-W${String(wk).padStart(2, "0")}`, sort: d.getFullYear() * 100 + wk };
    };
    // Buckets carry `appeared` and the earliest exam date as well as the running totals: the
    // trend is weighted by sittings (a 500-student paper shouldn't count the same as a
    // 5-student one), and the axis labels a real date rather than "2026-W29".
    const weekMap = {};
    rows.forEach((r) => {
      const wk = weekKey(r.date); if (!wk) return;
      if (!weekMap[wk.key]) weekMap[wk.key] = { key: wk.key, sort: wk.sort, sumAvg: 0, sumPass: 0, n: 0, appeared: 0, wAvg: 0, firstDate: null };
      const w = weekMap[wk.key];
      const app = r.appeared || 0;
      w.sumAvg += r.avgPct || 0; w.sumPass += r.passPct || 0; w.n += 1;
      w.appeared += app; w.wAvg += (r.avgPct || 0) * app;
      const d = parseInt(r.date) || 0;
      if (d && (!w.firstDate || d < w.firstDate)) w.firstDate = d;
    });
    const weeks = Object.values(weekMap).sort((a, b) => a.sort - b.sort);

    // Month-wise performance + exam/appeared volume.
    const monthName = (epoch) => {
      let n = parseInt(epoch); if (!n) return null; if (n < 1e12) n *= 1000;
      const d = new Date(n);
      const m = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][d.getMonth()];
      return { key: `${m} ${d.getFullYear()}`, sort: d.getFullYear() * 100 + d.getMonth() };
    };
    const monthMap = {};
    rows.forEach((r) => {
      const mk = monthName(r.date); if (!mk) return;
      if (!monthMap[mk.key]) monthMap[mk.key] = { key: mk.key, sort: mk.sort, sumAvg: 0, sumPass: 0, n: 0, appeared: 0, wAvg: 0, firstDate: null };
      const m = monthMap[mk.key];
      const app = r.appeared || 0;
      m.sumAvg += r.avgPct || 0; m.sumPass += r.passPct || 0; m.n += 1;
      m.appeared += app; m.wAvg += (r.avgPct || 0) * app;
      const d = parseInt(r.date) || 0;
      if (d && (!m.firstDate || d < m.firstDate)) m.firstDate = d;
    });
    const months = Object.values(monthMap).sort((a, b) => a.sort - b.sort);
    const volOptions = {
      chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit", stacked: false },
      plotOptions: { bar: { borderRadius: 4, columnWidth: "55%" } },
      colors: ["#06b6d4", "#f59e0b"], dataLabels: { enabled: false },
      xaxis: { categories: months.map((m) => m.key), labels: { style: { fontSize: "11px" } } },
      yaxis: [{ title: { text: "Exams" } }], legend: { position: "top" }, grid: { borderColor: "#f1f5f9" }, noData: { text: "No data" },
    };
    const volSeries = [
      { name: "Exams", data: months.map((m) => m.n) },
      { name: "Appeared", data: months.map((m) => m.appeared) },
    ];
    // Unified performance trend — Week or Month based on the toggle.
    const isWeek = this.state.trendMode === "week";
    const trendBuckets = isWeek ? weeks : months;
    // Weighted by sittings: a bucket's average is the average a STUDENT experienced, not the
    // average of the exam averages. The unweighted version let a 5-student paper swing the
    // line as hard as a 500-student one.
    const trendVals = trendBuckets.map((b) => (b.appeared ? Math.round(b.wAvg / b.appeared)
      : (b.n ? Math.round(b.sumAvg / b.n) : null)));
    // Weeks labelled by the date the week's first exam fell on — "2026-W29" means nothing to
    // a reader. Months already read as "Jul 2026".
    const dayLabel = (epoch) => {
      let n = parseInt(epoch); if (!n) return ""; if (n < 1e12) n *= 1000;
      const d = new Date(n);
      return `${String(d.getDate()).padStart(2, "0")} ${["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][d.getMonth()]}`;
    };
    const trendCats = trendBuckets.map((b) => (isWeek ? (b.firstDate ? dayLabel(b.firstDate) : b.key) : b.key));
    const trendOptions = {
      chart: { type: "line", toolbar: { show: false }, fontFamily: "inherit", zoom: { enabled: false } },
      // Straight, not smooth: these are discrete weekly/monthly points. A spline invents
      // values between them and can overshoot past the real min/max.
      stroke: { width: 2, curve: "straight" },
      colors: ["#7c3aed"],
      dataLabels: { enabled: false },
      // Markers are the hit target as well as the mark, so they need real size.
      markers: { size: 5, strokeWidth: 2, strokeColors: "#fff", hover: { size: 7 } },
      xaxis: {
        categories: trendCats,
        // Only rotate once there are enough points to need it.
        labels: { rotate: trendCats.length > 8 ? -45 : 0, rotateAlways: false, style: { fontSize: "11px", colors: "#64748b" } },
        axisBorder: { color: "#e2e8f0" }, axisTicks: { color: "#e2e8f0" },
      },
      yaxis: { min: 0, max: 100, tickAmount: 5, labels: { style: { colors: "#94a3b8" }, formatter: (v) => `${Math.round(v)}%` } },
      // One series — the card title already names it, so a legend box is just noise.
      legend: { show: false },
      grid: { borderColor: "#f1f5f9", strokeDashArray: 0 },
      // The average alone isn't interpretable; the tooltip carries what it was computed from.
      tooltip: {
        y: {
          formatter: (v, { dataPointIndex }) => {
            const b = trendBuckets[dataPointIndex] || {};
            return `${v == null ? "—" : `${v}%`}  ·  ${b.n || 0} exam${b.n === 1 ? "" : "s"} · ${(b.appeared || 0).toLocaleString()} sittings`;
          },
        },
      },
      noData: { text: "No data" },
    };
    const trendSeries = [{ name: "Avg %", data: trendVals }];

    const pct = (v, c) => <span style={{ fontWeight: 700, color: c }}>{v}%</span>;
    const fmtPct = (v) => (v == null ? 0 : v);

    // All-Exams table: search → sort → paginate. Searching and sorting run over the FULL
    // set (not just the visible page) so results aren't limited to the current page.
    // Clamp the page in case the row count shrank (narrower filter/search) while sitting
    // on a later page.
    const examQuery = (this.state.examSearch || "").trim().toLowerCase();
    const examFiltered = examQuery
      ? rows.filter((r) => [r.name, r.className].some(
        (v) => String(v || "").toLowerCase().includes(examQuery)))
      : rows;
    const examSorted = sortRows(examFiltered, this.state.examSort.sortKey, this.state.examSort.sortDir);
    const examTotal = examSorted.length;
    const examSize = this.state.examPageSize;
    const examPages = Math.max(1, Math.ceil(examTotal / examSize));
    const examPage = Math.min(this.state.examPage, examPages);
    const examStart = (examPage - 1) * examSize;
    const examPageRows = examSorted.slice(examStart, examStart + examSize);

    // ── Overall totals row ──────────────────────────────────────────────────────
    // Computed over every exam the table represents (examFiltered — i.e. across ALL
    // pages, and honouring the search), not just the page on screen.
    // Avg/Pass are weighted by `appeared`: a 200-student exam should count more than a
    // 12-student one, so a plain mean of the per-exam percentages would be misleading.
    // High/Low take the true max/min across exams, ignoring any nobody actually sat.
    const ftAgg = aggregateExams(examFiltered);
    const tStudents = ftAgg.students;
    const tAppeared = ftAgg.appeared;
    const tAvg = ftAgg.avg;
    const tError = ftAgg.error;
    const tHigh = ftAgg.high;
    const tLow = ftAgg.low;
    const tTurnout = ftAgg.turnout;

    // Reports | Comparisons switch — the comparison view is a self-contained sub-tab.
    const viewTab = (key, label, icon) => {
      const on = this.state.view === key;
      return (
        <button key={key} type="button" onClick={() => this.setState({ view: key })}
          style={{ display: "inline-flex", alignItems: "center", gap: 8, border: "none", cursor: "pointer", padding: "10px 18px", borderRadius: 10, fontSize: 13.5, fontWeight: 700, background: on ? "linear-gradient(135deg,#7c3aed,#6d28d9)" : "transparent", color: on ? "#fff" : "#726f84", boxShadow: on ? "0 6px 14px -6px rgba(124,58,237,.5)" : "none" }}>
          <i className={icon} /> {label}
        </button>
      );
    };
    const viewSwitch = (
      <div style={{ display: "inline-flex", background: "#fff", border: "1px solid #e9e6f4", borderRadius: 13, padding: 5, gap: 4, marginBottom: 16, boxShadow: "0 1px 2px rgba(24,16,48,.04)" }}>
        {viewTab("reports", "Exam Reports", "fal fa-file-chart-line")}
        {viewTab("compare", "Comparisons", "fal fa-balance-scale")}
      </div>
    );

    if (this.state.view === "compare") {
      return (
        <div style={S.page}>
          {viewSwitch}
          <ExamComparisonsTab scope={this.props.scope} embedded />
        </div>
      );
    }

    return (
      <div style={S.page}>
        {viewSwitch}
        <div style={{ marginBottom: 18, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 46, height: 46, borderRadius: 13, flex: "none", display: "grid", placeItems: "center", fontSize: 20, color: "#fff", background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)", boxShadow: "0 6px 16px -6px rgba(124,58,237,0.55)" }}>
              <i className="fal fa-file-chart-line" />
            </div>
            <div>
              <h1 style={S.h1}>Exam Reports</h1>
              <div style={S.sub}>Overall performance, branch comparison and subject strengths — across the selected scope</div>
            </div>
          </div>
          <ExportButtons onExcel={this.handleExcel} onPdf={this.handlePdf} disabled={loading} />
        </div>

        {/* Filters — cascading State -> City -> Branch -> Class -> Section */}
        <ScopeFilters scope={this.props.scope} sel={this.state.sel} onChange={this.setSel} style={{ ...S.card, ...S.filters }} />

        {/* KPIs — whole-selection summary */}
        <div style={S.kpiGrid}>
          {[
            { label: "Exams", value: overall.exams, color: "#7c3aed", icon: "fal fa-file-alt", hint: "in this scope" },
            { label: "Students", value: overall.students.toLocaleString(), color: "#2563eb", icon: "fal fa-users", hint: "on the rolls" },
            { label: "Appeared", value: overall.appeared.toLocaleString(), color: "#0891b2", icon: "fal fa-pen", hint: "total sittings" },
            { label: "Turnout", value: `${overall.turnout}%`, color: "#0d9488", icon: "fal fa-percentage", hint: "appeared ÷ students" },
            { label: "Avg Score", value: `${overall.avg}%`, color: "#7c3aed", icon: "fal fa-bullseye", hint: "weighted by appeared" },
            { label: "Highest", value: `${overall.high}%`, color: "#16a34a", icon: "fal fa-arrow-up", hint: "best exam average" },
            { label: "Lowest", value: `${overall.low}%`, color: "#dc2626", icon: "fal fa-arrow-down", hint: "weakest exam" },
            { label: "Avg Error", value: `${overall.error}%`, color: "#ea580c", icon: "fal fa-exclamation-triangle", hint: "answered wrong" },
          ].map((k) => (
            <div key={k.label} style={{ ...S.card, ...S.kpiCard }}>
              <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: k.color }} />
              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <i className={k.icon} style={{ fontSize: 12, color: k.color }} />
                <span style={S.kpiLabel}>{k.label}</span>
              </div>
              <div style={{ ...S.kpiValue, color: k.color }}>{loading ? "…" : k.value}</div>
              {k.hint && <div style={{ fontSize: 10.5, color: "#a29fb4", marginTop: 5 }}>{k.hint}</div>}
            </div>
          ))}
        </div>

        {/* Performance trend — Week / Month toggle */}
        <div style={{ ...S.card, ...S.cardPad, marginBottom: 16, position: "relative" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 8 }}>
            <div style={S.cardTitle}>Performance Trend — Avg %</div>
            <div style={{ display: "inline-flex", background: "#f1f5f9", borderRadius: 8, padding: 2 }}>
              {[{ k: "week", label: "Week-wise" }, { k: "month", label: "Month-wise" }].map((m) => {
                const on = this.state.trendMode === m.k;
                return (
                  <button key={m.k} onClick={() => this.setState({ trendMode: m.k })}
                    style={{ border: "none", borderRadius: 6, padding: "6px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer", background: on ? "#7c3aed" : "transparent", color: on ? "#fff" : "#475569" }}>
                    {m.label}
                  </button>
                );
              })}
            </div>
          </div>
          {loading && <div style={S.loadingOverlay}><Spinner /></div>}
          <ReactApexChart options={trendOptions} series={trendSeries} type="line" height={300} />
        </div>

        {/* Overall score-band distribution */}
        {this.renderScoreDistribution(rows)}

        {/* Branch-wise comparison — table + chart + ranking */}
        {this.renderBranchCompare()}

        {/* Subject performance by branch */}
        {this.renderSubjectMatrix()}

        {/* Detailed table — click a row for student results */}
        <div style={{ ...S.card, ...S.cardPad }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
            <div style={{ ...S.cardTitle, marginBottom: 0 }}>
              All Exams ({examQuery ? `${examTotal} of ${totalExams}` : totalExams}){" "}
              <span style={{ fontSize: 11, fontWeight: 500, color: "#94a3b8" }}>· click a row for student results</span>
            </div>
            <div style={{ position: "relative" }}>
              <i className="fal fa-search" style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: "#94a3b8", pointerEvents: "none" }} />
              <input
                type="text"
                value={this.state.examSearch}
                onChange={(e) => this.setExamSearch(e.target.value)}
                placeholder="Search exam or class…"
                style={{ border: "1.5px solid #e5e7eb", borderRadius: 9, padding: "8px 30px 8px 30px", fontSize: 13, color: "#1f2937", background: "#f9fafb", minWidth: 240, outline: "none" }}
              />
              {this.state.examSearch && (
                <i className="fal fa-times" title="Clear"
                  onClick={() => this.setExamSearch("")}
                  style={{ position: "absolute", right: 11, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: "#94a3b8", cursor: "pointer" }} />
              )}
            </div>
          </div>
          {loading ? (
            <TableShimmer rows={8} cols={6} />
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
                <thead style={{ background: "#fff" }}>
                  <tr>
                    <th style={S.th}>#</th>
                    <SortTh label="Exam" sortKey="name" state={this.state.examSort} onSort={this.setExamSort} style={S.th} />
                    <SortTh label="Class" sortKey="className" state={this.state.examSort} onSort={this.setExamSort} style={S.th} />
                    <SortTh label="Start" sortKey="date" state={this.state.examSort} onSort={this.setExamSort} style={S.th} />
                    <SortTh label="End" sortKey="endDate" state={this.state.examSort} onSort={this.setExamSort} style={S.th} />
                    <SortTh label="Students" sortKey="totalStudents" state={this.state.examSort} onSort={this.setExamSort} style={S.th} center />
                    <SortTh label="Appeared" sortKey="appeared" state={this.state.examSort} onSort={this.setExamSort} style={S.th} center />
                    <SortTh label="Qs" sortKey="totalQuestions" state={this.state.examSort} onSort={this.setExamSort} style={S.th} center />
                    <SortTh label="Avg %" sortKey="avgPct" state={this.state.examSort} onSort={this.setExamSort} style={S.th} center />
                    <SortTh label="High %" sortKey="highPct" state={this.state.examSort} onSort={this.setExamSort} style={S.th} center />
                    <SortTh label="Low %" sortKey="lowPct" state={this.state.examSort} onSort={this.setExamSort} style={S.th} center />
                    <SortTh label="Error %" sortKey="errorPct" state={this.state.examSort} onSort={this.setExamSort} style={S.th} center />
                  </tr>
                </thead>
                <tbody>
                  {examPageRows.length === 0 ? (
                    <tr><td colSpan={12} style={S.empty}>
                      {examQuery ? `No exams match "${this.state.examSearch}".` : "No exam data for this selection."}
                    </td></tr>
                  ) : examPageRows.map((r, i) => (
                    <tr key={r.id} onClick={() => this.openExam(r.id)} style={{ cursor: "pointer" }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#faf5ff")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                      <td style={S.td}>{examStart + i + 1}</td>
                      <td style={{ ...S.td, fontWeight: 600, color: "#7c3aed" }}>{r.name}</td>
                      <td style={S.td}>{r.className || "—"}</td>
                      <td style={S.td}>{fmtDateTime(r.date)}</td>
                      <td style={S.td}>{fmtDateTime(r.endDate)}</td>
                      <td style={{ ...S.td, textAlign: "center", fontWeight: 600 }}>{r.totalStudents || "—"}</td>
                      <td style={{ ...S.td, textAlign: "center" }}>{r.appeared}</td>
                      <td style={{ ...S.td, textAlign: "center" }}>{r.totalQuestions || "—"}</td>
                      <td style={{ ...S.td, textAlign: "center" }}>{pct(r.avgPct, "#7c3aed")}</td>
                      <td style={{ ...S.td, textAlign: "center" }}>{pct(r.highPct, "#16a34a")}</td>
                      <td style={{ ...S.td, textAlign: "center" }}>{pct(r.lowPct, "#dc2626")}</td>
                      <td style={{ ...S.td, textAlign: "center" }}><ErrPill pct={r.errorPct || 0} /></td>
                    </tr>
                  ))}
                </tbody>
                {examTotal > 0 && (() => {
                  const ft = { ...S.td, borderTop: "2px solid #e2e8f0", borderBottom: "none", background: "#f8fafc", fontWeight: 800, color: "#0f172a" };
                  const ftC = { ...ft, textAlign: "center" };
                  return (
                    <tfoot>
                      <tr>
                        <td colSpan={5} style={{ ...ft, textAlign: "right" }}>
                          Overall — {examTotal} exam{examTotal === 1 ? "" : "s"}
                          <span style={{ fontWeight: 500, color: "#94a3b8", fontSize: 11 }}>
                            {" "}· turnout {tTurnout}%
                          </span>
                        </td>
                        <td style={ftC}>{tStudents ? tStudents.toLocaleString() : "—"}</td>
                        <td style={ftC}>{tAppeared.toLocaleString()}</td>
                        <td style={{ ...ftC, color: "#94a3b8", fontWeight: 500 }}>—</td>
                        <td style={{ ...ftC, color: "#7c3aed" }}>{tAvg}%</td>
                        <td style={{ ...ftC, color: "#16a34a" }}>{tHigh}%</td>
                        <td style={{ ...ftC, color: "#dc2626" }}>{tLow}%</td>
                        <td style={ftC}><ErrPill pct={tError} /></td>
                      </tr>
                    </tfoot>
                  );
                })()}
              </table>
            </div>
          )}
          {!loading && this.renderExamPager(examTotal, examPage, examPages, examStart, examPageRows.length)}
        </div>

        {this.renderExamModal(fmtPct)}
      </div>
    );
  }

  // Recompute the exam summary over an arbitrary subset of takers (used by the section
  // filter). maxMarks / marksPerCorrect / marksPerWrong belong to the paper, not the
  // cohort, so they're carried over untouched.
  //
  // Rank and percentile are re-derived WITHIN the subset: pick a section and you are
  // ranked against that section only. Same rules the backend uses exam-wide — standard
  // competition ranking (equal marks share a rank, then the next rank skips), and
  // percentile = share of the cohort scoring strictly below you.
  deriveView = (d, subset) => {
    const mean = (a) => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : 0);
    const r2 = (n) => Math.round(n * 100) / 100;

    const ranked = [...subset].sort(
      (a, b) => (Number(b.marks) || 0) - (Number(a.marks) || 0) || (Number(b.correct) || 0) - (Number(a.correct) || 0)
    );
    const n = ranked.length;
    const sortedAsc = ranked.map((s) => Number(s.marks) || 0).slice().sort((a, b) => a - b);
    // # scoring strictly below m — lower-bound binary search over the ascending marks.
    const countBelow = (m) => {
      let lo = 0, hi = n;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (sortedAsc[mid] < m) lo = mid + 1; else hi = mid;
      }
      return lo;
    };
    let curRank = 0, prevMarks = null;
    const students = ranked.map((s, i) => {
      const m = Number(s.marks) || 0;
      if (prevMarks === null || m !== prevMarks) { curRank = i + 1; prevMarks = m; }
      return { ...s, rank: curRank, percentile: n ? Math.round((countBelow(m) / n) * 100) : 0 };
    });

    const marks = students.map((s) => Number(s.marks) || 0);
    const pcts = students.map((s) => Number(s.pct) || 0);
    return {
      ...d,
      students,
      appeared: students.length,
      avgMarks: r2(mean(marks)),
      highMarks: marks.length ? Math.max(...marks) : 0,
      lowMarks: marks.length ? Math.min(...marks) : 0,
      avgPct: Math.round(mean(pcts)),
      highPct: pcts.length ? Math.max(...pcts) : 0,
      lowPct: pcts.length ? Math.min(...pcts) : 0,
      subjectAvg: (d.subjectAvg || []).map((row) => {
        const got = students
          .map((s) => (s.subjects || []).find((x) => x.subject === row.subject))
          .filter((m) => m && m.total);
        const avgMarks = mean(got.map((m) => Number(m.marks) || 0));
        return {
          subject: row.subject,
          maxMarks: row.maxMarks,
          avgMarks: r2(avgMarks),
          pct: row.maxMarks ? Math.round((avgMarks / row.maxMarks) * 100) : 0,
        };
      }),
    };
  };

  // Question-wise Correct / Wrong / Unattempted for the exam (and the selected section).
  renderQuestionAnalysis = () => {
    const { qa, qaLoading, qaSort, detailSection, qaError, qaSubject, qaMetric } = this.state;
    if (qaLoading) return <ModalFormShimmer rows={6} />;
    if (qaError) {
      const stale = /Cannot query field|GRAPHQL_VALIDATION_FAILED|Unknown/i.test(qaError);
      return (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "20px 24px", color: "#991b1b", fontSize: 13, lineHeight: 1.7 }}>
          <div style={{ fontWeight: 800, marginBottom: 6 }}>Couldn’t load question analysis</div>
          {stale && (
            <div style={{ marginBottom: 8 }}>
              The API is running an older schema. <b>Restart the backend</b> — <code>.graphql</code> files don’t hot-reload.
            </div>
          )}
          <code style={{ display: "block", background: "#fff", border: "1px solid #fecaca", borderRadius: 6, padding: "8px 10px", fontSize: 11, color: "#7f1d1d", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {qaError}
          </code>
          <button type="button" onClick={this.loadQuestionAnalysis}
            style={{ marginTop: 10, border: "none", background: "#dc2626", color: "#fff", borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            Retry
          </button>
        </div>
      );
    }
    if (!qa || !(qa.questions || []).length) {
      return (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 32, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
          No question-level data for this exam{detailSection !== "ALL" ? ` in ${detailSection}` : ""}.
        </div>
      );
    }
    const td = { fontSize: 13, color: "#334155", padding: "8px 10px", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" };
    const th = { textAlign: "left", fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", padding: "8px 10px", borderBottom: "2px solid #e2e8f0", whiteSpace: "nowrap" };

    // Subject filter. Every question already carries its subject, so this is a pure
    // client-side narrowing — no refetch. The totals cards recompute for the subset, so
    // "Physics" answers "how did the batch do in Physics?" rather than just hiding rows.
    const allQ = qa.questions || [];
    const subjectOptions = [...new Set(allQ.map((q) => q.subject).filter(Boolean))].sort();
    const subFilter = qaSubject || "ALL";
    const filtered = subFilter === "ALL" ? allQ : allQ.filter((q) => q.subject === subFilter);

    const sum = (k) => filtered.reduce((a, q) => a + (q[k] || 0), 0);
    const tCorrect = sum("correct");
    const tWrong = sum("wrong");
    const tUnatt = sum("unattempted");
    const tAll = tCorrect + tWrong + tUnatt;
    const p = (a, b) => (b ? Math.round((a / b) * 100) : 0);
    const view = subFilter === "ALL" ? qa : {
      ...qa,
      totalQuestions: filtered.length,
      totalCorrect: tCorrect,
      totalWrong: tWrong,
      totalUnattempted: tUnatt,
      correctPct: p(tCorrect, tAll),
      wrongPct: p(tWrong, tAll),
      unattemptedPct: p(tUnatt, tAll),
      errorPct: p(tWrong + tUnatt, tAll),
    };

    // Subject-wise rollup — percentages are of that subject's total answers
    // (its questions × the takers), so the three always add to 100. Worst subject first,
    // which is the whole point: it names the subject to act on.
    const subjectStats = subjectOptions.map((name) => {
      const qs = allQ.filter((q) => q.subject === name);
      const c = qs.reduce((a, q) => a + (q.correct || 0), 0);
      const w = qs.reduce((a, q) => a + (q.wrong || 0), 0);
      const u = qs.reduce((a, q) => a + (q.unattempted || 0), 0);
      const tot = c + w + u;
      return {
        name, questions: qs.length,
        correct: c, wrong: w, unattempted: u,
        correctPct: p(c, tot), wrongPct: p(w, tot), unattemptedPct: p(u, tot),
        errorPct: p(w + u, tot),
      };
    }).sort((a, b) => b.errorPct - a.errorPct);
    // The grid follows the Subject dropdown, same as the table does.
    const shownSubjects = subFilter === "ALL"
      ? subjectStats
      : subjectStats.filter((s) => s.name === subFilter);
    // Which outcome the bands measure: Wrong (default), Correct, or Unattempted.
    const metric = qaMetric || "wrong";
    const M = QA_METRICS[metric] || QA_METRICS.wrong;
    const BANDS = qBands(M.good);

    // Default to worst-first: the point of the tab is to find the questions that hurt, and
    // paper order buries them. Any explicit sort click takes over.
    const rows = qaSort.sortKey
      ? sortRows(filtered, qaSort.sortKey, qaSort.sortDir)
      : [...filtered].sort((a, b) => b.errorPct - a.errorPct || a.slno - b.slno);

    const C = { correct: "#16a34a", wrong: "#dc2626", un: "#94a3b8" };
    return (
      <>
        {/* Question HTML is authored elsewhere and can carry big diagrams / block <p> margins.
            Clamp it to two lines and cap image height so every row stays the same size. */}
        <style>{`
          .qa-qtext { max-height: 3.2em; overflow: hidden; font-size: 12.5px; line-height: 1.6; color: #334155; }
          .qa-qtext p { margin: 0 !important; display: inline; }
          .qa-qtext img { max-height: 26px; max-width: 100%; width: auto; vertical-align: middle; }
          .qa-qtext table { display: none; }
        `}</style>

        {/* Plain-language explainer — the tab has to read on its own, without training. */}
        <div style={{ background: "#faf5ff", border: "1px solid #e9d5ff", borderRadius: 10, padding: "10px 14px", marginBottom: 12, fontSize: 12, color: "#4c1d95", lineHeight: 1.6 }}>
          <b>How to read this:</b> every one of the <b>{qa.takers}</b> students who wrote this exam either got a
          question <span style={{ color: C.correct, fontWeight: 700 }}>right</span>,
          got it <span style={{ color: C.wrong, fontWeight: 700 }}>wrong</span>, or
          left it <span style={{ color: "#64748b", fontWeight: 700 }}>blank</span> — the three always add up to {qa.takers}.
          <br />
          <b>Error %</b> = the share who <i>answered it wrong</i> (blanks are not counted). It's the column to watch:
          <span style={{ color: "#16a34a", fontWeight: 700 }}> green under 50%</span>,
          <span style={{ color: "#ea580c", fontWeight: 700 }}> orange 50–70%</span>,
          <span style={{ color: "#dc2626", fontWeight: 700 }}> red above 70%</span>.
          Worst questions are listed first — those are the topics to re-teach.
        </div>

        {/* Subject filter — narrows the table AND the totals below */}
        {subjectOptions.length > 1 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 14px", marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.3 }}>Subject</span>
              <select value={subFilter} onChange={(e) => this.setState({ qaSubject: e.target.value })}
                style={{ border: "1.5px solid #e5e7eb", borderRadius: 8, padding: "6px 10px", fontSize: 13, color: "#1f2937", background: "#fff", cursor: "pointer", minWidth: 170 }}>
                <option value="ALL">All Subjects ({allQ.length})</option>
                {subjectOptions.map((sub) => (
                  <option key={sub} value={sub}>{sub} ({allQ.filter((q) => q.subject === sub).length})</option>
                ))}
              </select>
            </div>
            <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 500 }}>
              {subFilter === "ALL"
                ? `All ${allQ.length} questions`
                : `${filtered.length} of ${allQ.length} questions · figures below are for ${subFilter}`}
            </div>
          </div>
        )}

        {/* Totals across the visible questions, plus their overall error rate */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px,1fr))", gap: 10, marginBottom: 12 }}>
          {[
            { label: subFilter === "ALL" ? "Paper" : subFilter, value: view.totalQuestions, color: "#0f172a", sub: `questions × ${qa.takers} students` },
            { label: "Answered right", value: view.totalCorrect.toLocaleString(), color: C.correct, sub: `${view.correctPct}% of all answers` },
            { label: "Answered wrong", value: view.totalWrong.toLocaleString(), color: C.wrong, sub: `${view.wrongPct}% of all answers` },
            { label: "Left blank", value: view.totalUnattempted.toLocaleString(), color: C.un, sub: `${view.unattemptedPct}% of all answers` },
            { label: "Error Rate", value: `${view.errorPct}%`, color: errBand(view.errorPct).color, sub: "wrong answers only" },
          ].map((k) => (
            <div key={k.label} style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 12px" }}>
              <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>{k.label}</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: k.color }}>{k.value}</div>
              <div style={{ fontSize: 11, color: "#94a3b8" }}>{k.sub}</div>
            </div>
          ))}
        </div>

        {/* Subject-wise analysis — questions bucketed by how much of the batch got them
            right, per subject. A band row answers "how many Physics questions did 0-19% of
            the class get right?" — which is where the re-teaching list comes from. */}
        {subjectStats.length > 1 && (
          <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: "12px 14px", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                Subject-wise Analysis
                <span style={{ fontSize: 11, fontWeight: 500, color: "#94a3b8" }}>
                  {" "}· questions by % of students who <b style={{ color: M.good ? C.correct : (metric === "unattempted" ? "#64748b" : C.wrong) }}>{M.verb}</b>
                  {subFilter === "ALL" ? " · click a subject to filter" : ` · ${subFilter} only`}
                </span>
              </div>
              {/* Which outcome the bands measure */}
              <select value={metric} onChange={(e) => this.setState({ qaMetric: e.target.value })}
                style={{ border: "1.5px solid #e5e7eb", borderRadius: 8, padding: "5px 10px", fontSize: 12, fontWeight: 700, color: "#1f2937", background: "#fff", cursor: "pointer" }}>
                <option value="wrong">Wrong</option>
                <option value="correct">Correct</option>
                <option value="unattempted">Unattempted</option>
              </select>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 520 }}>
                <thead>
                  <tr>
                    <th style={{ ...th, width: 96 }}>Band</th>
                    {/* The grid follows the Subject dropdown: pick a subject and it narrows to
                        that column, so the block agrees with the table below it. */}
                    {subFilter === "ALL" && <th style={{ ...th, textAlign: "center" }}>All</th>}
                    {shownSubjects.map((s) => (
                      <th key={s.name}
                        onClick={() => this.setState({ qaSubject: subFilter === s.name ? "ALL" : s.name })}
                        style={{ ...th, textAlign: "center", cursor: "pointer", color: subFilter === s.name ? "#7c3aed" : "#64748b" }}
                        title={`${s.questions} questions · ${s.errorPct}% error rate${subFilter === s.name ? " · click to show all subjects" : ""}`}>
                        {s.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {BANDS.map((b) => {
                    // Questions whose chosen-outcome % falls in this band. The metric dropdown
                    // picks which outcome: wrongPct, correctPct or unattemptedPct — each is a
                    // share of takers, so the three are directly comparable.
                    const cell = (qs) => {
                      const hit = qs.filter((q) => q[M.key] >= b.min && q[M.key] < b.max);
                      return {
                        n: hit.length,
                        pct: qs.length ? Math.round((hit.length / qs.length) * 100) : 0,
                        nos: numRanges(hit.map((q) => q.slno)),
                      };
                    };
                    const all = cell(allQ);
                    const cs = { ...td, textAlign: "center", padding: "7px 10px", verticalAlign: "top" };
                    // The question numbers, clamped to two lines — full list on hover.
                    const qNos = (c) => (
                      c.n ? (
                        <div title={`Q ${c.nos}`}
                          style={{ fontSize: 10.5, color: "#94a3b8", marginTop: 3, lineHeight: 1.4, maxHeight: "2.8em", overflow: "hidden", wordBreak: "break-word" }}>
                          Q {c.nos}
                        </div>
                      ) : null
                    );
                    return (
                      <tr key={b.label}>
                        <td style={{ ...td, padding: "7px 10px", verticalAlign: "top" }}>
                          <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: b.color, marginRight: 6 }} />
                          <span style={{ fontSize: 11, color: "#64748b" }}>{b.label}</span>
                        </td>
                        {subFilter === "ALL" && (
                          <td style={cs}>
                            <span style={{ fontWeight: 700, color: all.n ? b.color : "#cbd5e1" }}>{all.n}</span>{" "}
                            <span style={{ color: "#94a3b8" }}>({all.pct}%)</span>
                            {qNos(all)}
                          </td>
                        )}
                        {shownSubjects.map((s) => {
                          const c = cell(allQ.filter((q) => q.subject === s.name));
                          return (
                            <td key={s.name} style={cs}>
                              <span style={{ fontWeight: 700, color: c.n ? b.color : "#cbd5e1" }}>{c.n}</span>{" "}
                              <span style={{ color: "#94a3b8" }}>({c.pct}%)</span>
                              {qNos(c)}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  {/* Overall rate for whichever outcome is selected. */}
                  <tr>
                    <td style={{ ...td, padding: "7px 10px", borderTop: "2px solid #e2e8f0", fontWeight: 800, color: "#0f172a", fontSize: 11 }}>
                      {M.label} rate
                    </td>
                    {subFilter === "ALL" && (
                      <td style={{ ...td, textAlign: "center", padding: "7px 10px", borderTop: "2px solid #e2e8f0" }}>
                        <RatePill pct={qa[M.key]} good={M.good} />
                      </td>
                    )}
                    {shownSubjects.map((s) => (
                      <td key={s.name} style={{ ...td, textAlign: "center", padding: "7px 10px", borderTop: "2px solid #e2e8f0" }}>
                        <RatePill pct={s[M.key]} good={M.good} />
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: "8px 4px", maxHeight: 460, overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
            <thead><tr>
              <SortTh label="Q#" sortKey="slno" state={qaSort} onSort={this.setQaSort} style={th} />
              <SortTh label="Subject" sortKey="subject" state={qaSort} onSort={this.setQaSort} style={th} />
              <th style={{ ...th, textAlign: "left", minWidth: 260 }}>Question</th>
              <SortTh label="Correct" sortKey="correct" state={qaSort} onSort={this.setQaSort} style={th} center />
              <SortTh label="Wrong" sortKey="wrong" state={qaSort} onSort={this.setQaSort} style={th} center />
              <SortTh label="Unattempted" sortKey="unattempted" state={qaSort} onSort={this.setQaSort} style={th} center />
              <SortTh label="Error %" sortKey="errorPct" state={qaSort} onSort={this.setQaSort} style={th} center />
            </tr></thead>
            <tbody>
              {rows.map((q) => (
                <tr key={q.questionId}>
                  <td style={{ ...td, fontWeight: 700 }}>{q.slno}</td>
                  <td style={td}>{q.subject || "—"}</td>
                  {/* Question text is authored HTML: formulae and diagrams are <img> tags (MathML
                      renders, plus some base64 diagrams), so it has to be rendered, not escaped.
                      The wrapper clamps image height so a diagram can't blow the row open. */}
                  <td style={{ ...td, whiteSpace: "normal", maxWidth: 380 }} title={q.questionText || ""}>
                    {q.question ? (
                      <div className="qa-qtext" dangerouslySetInnerHTML={{ __html: q.question }} />
                    ) : (
                      <span style={{ color: "#cbd5e1" }}>—</span>
                    )}
                  </td>
                  <td style={{ ...td, textAlign: "center", color: C.correct, fontWeight: 700 }}>
                    {q.correct} <span style={{ color: "#94a3b8", fontWeight: 500 }}>({q.correctPct}%)</span>
                  </td>
                  <td style={{ ...td, textAlign: "center", color: C.wrong, fontWeight: 700 }}>
                    {q.wrong} <span style={{ color: "#94a3b8", fontWeight: 500 }}>({q.wrongPct}%)</span>
                  </td>
                  <td style={{ ...td, textAlign: "center", color: C.un, fontWeight: 700 }}>
                    {q.unattempted} <span style={{ color: "#cbd5e1", fontWeight: 500 }}>({q.unattemptedPct}%)</span>
                  </td>
                  <td style={{ ...td, textAlign: "center" }}><ErrPill pct={q.errorPct || 0} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  // ── Exam Analysis (deep dive) ───────────────────────────────────────────────
  // Per-exam analytics from mis-exam-analysis-v2.html: answer split, section
  // comparison, marks-vs-percentile spread and top/bottom performers. All of it is
  // derived from the detail payload the modal already loaded — no extra query.
  // Section comparison deliberately uses the EXAM-WIDE list so sections stay
  // comparable even when the modal is filtered down to a single section.
  renderExamAnalysis = (view, students, allStudents) => {
    const list = students || [];
    const num = (v) => Number(v) || 0;
    const card = { ...S.card, marginBottom: 16 };
    if (!list.length) {
      return (
        <div style={card}>
          <CardHead icon="fal fa-chart-scatter" title="Exam Analysis" sub="Deep dive" />
          <div style={S.cardPad}><div style={S.empty}>No student results for this selection.</div></div>
        </div>
      );
    }

    // 1. Answer split. Unattempted is inferred per student (total − correct − wrong)
    //    so it stays correct even when a student skipped an entire subject.
    let correct = 0, wrong = 0, unatt = 0;
    list.forEach((s) => {
      const c = num(s.correct), w = num(s.wrong), tq = num(s.totalQuestions);
      correct += c; wrong += w; unatt += Math.max(0, tq - c - w);
    });
    const totalAns = correct + wrong + unatt;
    const splits = [
      { label: "Correct", value: correct, color: "#16a34a" },
      { label: "Wrong", value: wrong, color: "#dc2626" },
      { label: "Unattempted", value: unatt, color: "#94a3b8" },
    ];
    const pc = (n) => (totalAns ? Math.round((n / totalAns) * 1000) / 10 : 0);

    // 2. Section comparison (exam-wide).
    const secMap = {};
    (allStudents || []).forEach((s) => {
      const k = s.sectionName || "—";
      if (!secMap[k]) secMap[k] = { name: k, n: 0, pctSum: 0, markSum: 0 };
      secMap[k].n += 1; secMap[k].pctSum += num(s.pct); secMap[k].markSum += num(s.marks);
    });
    const sections = Object.keys(secMap).map((k) => {
      const x = secMap[k];
      return { name: x.name, n: x.n, avgPct: x.n ? x.pctSum / x.n : 0 };
    }).sort((a, b) => b.avgPct - a.avgPct);
    const secMax = Math.max(1, ...sections.map((s) => s.avgPct));

    // 3. Marks vs percentile — shows whether the cohort is bunched or spread out.
    const pts = list
      .filter((s) => s.percentile != null && s.marks != null)
      .map((s) => ({ x: num(s.marks), y: num(s.percentile) }));
    const maxM = Math.max(1, num(view && view.maxMarks), ...pts.map((p) => p.x));

    // 4. Top & bottom by marks.
    const ranked = list.slice().sort((a, b) => num(b.marks) - num(a.marks));
    const top = ranked.slice(0, 5);
    const bottom = ranked.length > 5 ? ranked.slice(-5).reverse() : [];

    // 5. Rank analysis. `rank` and `percentile` follow the modal's section filter — on
    //    "All Sections" they are exam-wide, on a single section they are re-based to that
    //    cohort (see deriveView). So these two panels always describe what's on screen.
    const withRank = list.filter((s) => num(s.rank) > 0);
    const rankBands = [
      { label: "Top 10", color: "#16a34a", test: (r) => r <= 10 },
      { label: "11 – 25", color: "#22c55e", test: (r) => r > 10 && r <= 25 },
      { label: "26 – 50", color: "#f59e0b", test: (r) => r > 25 && r <= 50 },
      { label: "51 – 100", color: "#fb923c", test: (r) => r > 50 && r <= 100 },
      { label: "100+", color: "#dc2626", test: (r) => r > 100 },
    ].map((b) => ({ ...b, count: withRank.filter((s) => b.test(num(s.rank))).length }));
    const rankMax = Math.max(1, ...rankBands.map((b) => b.count));

    const withPct = list.filter((s) => s.percentile != null);
    const pctBands = [
      { label: "90+", color: "#16a34a", test: (p) => p >= 90 },
      { label: "75 – 90", color: "#22c55e", test: (p) => p >= 75 && p < 90 },
      { label: "50 – 75", color: "#f59e0b", test: (p) => p >= 50 && p < 75 },
      { label: "25 – 50", color: "#fb923c", test: (p) => p >= 25 && p < 50 },
      { label: "< 25", color: "#dc2626", test: (p) => p < 25 },
    ].map((b) => ({ ...b, count: withPct.filter((s) => b.test(num(s.percentile))).length }));
    const pctMax = Math.max(1, ...pctBands.map((b) => b.count));

    // Which sections produce the toppers. Deliberately uses the EXAM-WIDE ranks so the
    // comparison stays like-for-like — a section's own internal ranking would make every
    // section look identical (each would hold ranks 1..n).
    const CUTS = [10, 25, 50];
    const examRanked = (allStudents || []).filter((s) => num(s.rank) > 0);
    const topShare = sections.map((sec) => {
      const row = { name: sec.name, n: sec.n };
      CUTS.forEach((c) => {
        row[`t${c}`] = examRanked.filter((s) => num(s.rank) <= c && (s.sectionName || "—") === sec.name).length;
      });
      return row;
    }).sort((a, b) => b.t10 - a.t10 || b.t25 - a.t25 || b.t50 - a.t50);

    // Shared band-bar renderer for the rank + percentile panels.
    const bandBars = (bands, maxV, totalV) => (
      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
        {bands.map((b) => (
          <div key={b.label} style={{ display: "grid", gridTemplateColumns: "76px 1fr 116px", alignItems: "center", gap: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 750, color: b.color }}>{b.label}</div>
            <div style={{ background: "#f1eefb", borderRadius: 7, height: 20, overflow: "hidden" }}>
              <div style={{ width: `${(b.count / maxV) * 100}%`, height: "100%", borderRadius: 7, background: b.color, minWidth: b.count ? 4 : 0, transition: "width .3s" }} />
            </div>
            <div style={{ textAlign: "right", fontSize: 12, color: "#726f84" }}>
              <b style={{ color: "#1f1b2e" }}>{b.count}</b>
              <span style={{ color: "#a29fb4" }}> · {totalV ? Math.round((b.count / totalV) * 100) : 0}%</span>
            </div>
          </div>
        ))}
      </div>
    );

    const perfRow = (s, i, tone) => (
      <div key={`${tone}-${i}-${s.name || ""}`} style={{ display: "grid", gridTemplateColumns: "22px 1fr 64px", alignItems: "center", gap: 10, padding: "6px 0" }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "#a29fb4" }}>{i + 1}</span>
        <span style={{ fontSize: 12.5, color: "#1f1b2e", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {s.name || "—"}
          <span style={{ color: "#a29fb4" }}>{s.sectionName ? ` · ${s.sectionName}` : ""}</span>
        </span>
        <b style={{ textAlign: "right", fontSize: 12.5, color: tone }}>{Math.round(num(s.marks) * 10) / 10}</b>
      </div>
    );

    return (
      <React.Fragment>
        <div style={card}>
          <CardHead icon="fal fa-chart-pie" title="Answer Split" sub="Correct / wrong / unattempted across every answer in this cohort" />
          <div style={S.cardPad}>
            <div style={{ display: "flex", height: 26, borderRadius: 8, overflow: "hidden", background: "#f1eefb" }}>
              {splits.map((s) => (s.value > 0
                ? <div key={s.label} title={`${s.label}: ${s.value}`} style={{ width: `${pc(s.value)}%`, background: s.color }} />
                : null))}
            </div>
            <div style={{ display: "flex", gap: 18, flexWrap: "wrap", marginTop: 12 }}>
              {splits.map((s) => (
                <span key={s.label} style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12.5, color: "#726f84" }}>
                  <i style={{ width: 9, height: 9, borderRadius: 3, background: s.color, display: "inline-block" }} />
                  {s.label} <b style={{ color: "#1f1b2e" }}>{s.value}</b>
                  <span style={{ color: "#a29fb4" }}>· {pc(s.value)}%</span>
                </span>
              ))}
            </div>
          </div>
        </div>

        {sections.length > 1 && (
          <div style={card}>
            <CardHead icon="fal fa-layer-group" title="Section Comparison" sub="Average score per section, measured exam-wide" />
            <div style={S.cardPad}>
              <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                {sections.map((s) => (
                  <div key={s.name} style={{ display: "grid", gridTemplateColumns: "120px 1fr 118px", alignItems: "center", gap: 12 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: "#1f1b2e", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</div>
                    <div style={{ background: "#f1eefb", borderRadius: 7, height: 20, overflow: "hidden" }}>
                      <div style={{ width: `${(s.avgPct / secMax) * 100}%`, height: "100%", borderRadius: 7, background: "#7c3aed", minWidth: s.avgPct ? 4 : 0, transition: "width .3s" }} />
                    </div>
                    <div style={{ textAlign: "right", fontSize: 12, color: "#726f84" }}>
                      <b style={{ color: "#1f1b2e" }}>{Math.round(s.avgPct * 10) / 10}%</b>
                      <span style={{ color: "#a29fb4" }}> · {s.n} student{s.n === 1 ? "" : "s"}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {pts.length > 1 && (
          <div style={card}>
            <CardHead icon="fal fa-chart-scatter" title="Marks vs Percentile" sub="Each dot is a student — a steep cluster means the cohort is tightly bunched" />
            <div style={S.cardPad}>
              <svg viewBox="0 0 340 180" style={{ width: "100%", height: "auto" }}>
                {[0, 25, 50, 75, 100].map((g) => {
                  const y = 160 - (g / 100) * 145;
                  return (
                    <g key={g}>
                      <line x1="34" y1={y} x2="334" y2={y} stroke="#eef0f3" strokeWidth="1" />
                      <text x="28" y={y + 3} textAnchor="end" fontSize="8" fill="#a29fb4">{g}</text>
                    </g>
                  );
                })}
                {pts.map((p, i) => (
                  <circle key={i} cx={34 + (p.x / maxM) * 296} cy={160 - (p.y / 100) * 145} r="3" fill="#7c3aed" fillOpacity=".55" />
                ))}
                <text x="184" y="176" textAnchor="middle" fontSize="8" fill="#a29fb4">Marks (0 – {Math.round(maxM)}) · y-axis = percentile</text>
              </svg>
            </div>
          </div>
        )}

        {withRank.length > 0 && (
          <div style={card}>
            <CardHead icon="fal fa-ranking-star" title="Rank Distribution" sub="How many students fall in each rank band" />
            <div style={S.cardPad}>{bandBars(rankBands, rankMax, withRank.length)}</div>
          </div>
        )}

        {withPct.length > 0 && (
          <div style={card}>
            <CardHead icon="fal fa-percent" title="Percentile Bands" sub="Spread of the cohort across percentile ranges" />
            <div style={S.cardPad}>{bandBars(pctBands, pctMax, withPct.length)}</div>
          </div>
        )}

        {topShare.length > 1 && examRanked.length > 0 && (
          <div style={card}>
            <CardHead icon="fal fa-trophy" title="Top Ranks by Section" sub="Which sections hold the exam-wide top ranks" />
            <div style={{ ...S.cardPad, overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", padding: "8px 10px", fontSize: 10.5, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", color: "#a29fb4", borderBottom: "1px solid #e4e7ec" }}>Section</th>
                    {CUTS.map((c) => (
                      <th key={c} style={{ textAlign: "right", padding: "8px 10px", fontSize: 10.5, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", color: "#a29fb4", borderBottom: "1px solid #e4e7ec", whiteSpace: "nowrap" }}>Top {c}</th>
                    ))}
                    <th style={{ textAlign: "right", padding: "8px 10px", fontSize: 10.5, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", color: "#a29fb4", borderBottom: "1px solid #e4e7ec" }}>Students</th>
                  </tr>
                </thead>
                <tbody>
                  {topShare.map((r) => (
                    <tr key={r.name}>
                      <td style={{ padding: "9px 10px", borderBottom: "1px solid #eef0f3", fontWeight: 700, color: "#1f1b2e", whiteSpace: "nowrap" }}>{r.name}</td>
                      {CUTS.map((c) => (
                        <td key={c} style={{ padding: "9px 10px", borderBottom: "1px solid #eef0f3", textAlign: "right", color: r[`t${c}`] ? "#1f1b2e" : "#cbd5e1", fontWeight: r[`t${c}`] ? 700 : 400 }}>{r[`t${c}`]}</td>
                      ))}
                      <td style={{ padding: "9px 10px", borderBottom: "1px solid #eef0f3", textAlign: "right", color: "#726f84" }}>{r.n}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div style={card}>
          <CardHead icon="fal fa-medal" title="Top & Bottom" sub="Highest and lowest scorers in this selection" />
          <div style={{ ...S.cardPad, display: "grid", gridTemplateColumns: bottom.length ? "1fr 1fr" : "1fr", gap: 22 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", color: "#16a34a", marginBottom: 4 }}>Top performers</div>
              {top.map((s, i) => perfRow(s, i, "#16a34a"))}
            </div>
            {bottom.length > 0 && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", color: "#dc2626", marginBottom: 4 }}>Needs attention</div>
                {bottom.map((s, i) => perfRow(s, i, "#dc2626"))}
              </div>
            )}
          </div>
        </div>
      </React.Fragment>
    );
  };

  renderExamModal = () => {
    const { detail, detailLoading, detailTab } = this.state;
    if (!detail) return null;
    const d = detail;
    const allStudents = d.students || [];
    // Section filter. Rank + percentile stay EXAM-WIDE on purpose — your standing in the
    // exam doesn't change just because you're looking at one section. This only narrows
    // which rows are shown.
    const secFilter = this.state.detailSection || "ALL";
    const sectionOptions = d.sections || [];
    // Fixed subject column order (from the backend), so every student's row lines up.
    const subjectNames = d.subjectNames || [];
    // Pick a section and the WHOLE modal re-bases on it — KPIs, score distribution,
    // subject averages, and each student's rank + percentile (now against that section's
    // cohort rather than the full exam). "All Sections" uses the backend's exam-wide numbers.
    const view = secFilter === "ALL"
      ? d
      : this.deriveView(d, allStudents.filter((s) => (s.sectionName || "") === secFilter));
    const students = view.students || [];
    const distBuckets = [
      { label: "80-100%", min: 80, color: "#16a34a" },
      { label: "60-79%", min: 60, color: "#22c55e" },
      { label: "40-59%", min: 40, color: "#f59e0b" },
      { label: "20-39%", min: 20, color: "#fb923c" },
      { label: "0-19%", min: 0, color: "#dc2626" },
    ].map((b, i, arr) => {
      const max = i === 0 ? 101 : arr[i - 1].min;
      return { ...b, count: students.filter((s) => s.pct >= b.min && s.pct < max).length };
    });
    const td = { fontSize: 13, color: "#334155", padding: "8px 10px", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" };
    const th = { textAlign: "left", fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", padding: "8px 10px", borderBottom: "2px solid #e2e8f0", whiteSpace: "nowrap" };
    return (
      <Modal show={true} onHide={this.closeExam} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}>
            {d.name || "Exam"} <span style={{ fontSize: 12, fontWeight: 500, color: "#94a3b8" }}>· {d.className || "—"}</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "#f8fafc" }}>
          {detailLoading ? (
            <ModalFormShimmer rows={6} />
          ) : (
            <>
              {/* Section filter — sits at the top because it drives EVERYTHING below it:
                  KPIs, score distribution, subject averages and the student list. */}
              {sectionOptions.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 14px", marginBottom: 14 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.3 }}>Section</span>
                    <select value={secFilter} onChange={(e) => this.setDetailSection(e.target.value)}
                      style={{ border: "1.5px solid #e5e7eb", borderRadius: 8, padding: "6px 10px", fontSize: 13, color: "#1f2937", background: "#fff", cursor: "pointer", minWidth: 170 }}>
                      <option value="ALL">All Sections ({allStudents.length})</option>
                      {sectionOptions.map((sec) => {
                        const n = allStudents.filter((s) => (s.sectionName || "") === sec).length;
                        return <option key={sec} value={sec}>{sec} ({n})</option>;
                      })}
                    </select>
                  </div>
                  <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 500 }}>
                    {secFilter === "ALL"
                      ? `All ${allStudents.length} takers`
                      : `${students.length} of ${allStudents.length} takers · everything below is within ${secFilter}`}
                    {detailTab === "students" && (secFilter === "ALL"
                      ? " · rank & percentile across the full exam"
                      : ", including rank & percentile")}
                  </div>
                </div>
              )}

              {/* Students | Question Analysis */}
              <div style={{ display: "flex", gap: 6, borderBottom: "2px solid #e2e8f0", marginBottom: 14 }}>
                {[
                  { key: "students", label: "Students", icon: "fal fa-users" },
                  { key: "analysis", label: "Analysis", icon: "fal fa-chart-scatter" },
                  { key: "questions", label: "Question Analysis", icon: "fal fa-list-ol" },
                ].map((t) => {
                  const on = detailTab === t.key;
                  return (
                    <button key={t.key} type="button" onClick={() => this.setDetailTab(t.key)}
                      style={{
                        border: "none", background: "transparent", cursor: "pointer",
                        padding: "8px 14px", fontSize: 13, fontWeight: 700,
                        color: on ? "#7c3aed" : "#64748b",
                        borderBottom: `2px solid ${on ? "#7c3aed" : "transparent"}`,
                        marginBottom: -2,
                      }}>
                      <i className={t.icon} /> {t.label}
                    </button>
                  );
                })}
              </div>

              {detailTab === "questions" ? this.renderQuestionAnalysis()
                : detailTab === "analysis" ? this.renderExamAnalysis(view, students, allStudents) : (
              <>
              {/* Exam KPIs — recomputed for the selected section */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px,1fr))", gap: 10, marginBottom: 14 }}>
                {[
                  { label: "Appeared", value: view.appeared, color: "#2563eb" },
                  { label: "Max Marks", value: fmtMarks(view.maxMarks), color: "#0f172a" },
                  { label: "Avg Marks", value: fmtMarks(view.avgMarks), color: "#7c3aed" },
                  { label: "High Marks", value: fmtMarks(view.highMarks), color: "#16a34a" },
                  { label: "Low Marks", value: fmtMarks(view.lowMarks), color: "#dc2626" },
                  { label: "Avg %", value: `${view.avgPct}%`, color: "#7c3aed" },
                ].map((k) => (
                  <div key={k.label} style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 12px" }}>
                    <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>{k.label}</div>
                    <div style={{ fontSize: 20, fontWeight: 800, color: k.color }}>{k.value}</div>
                  </div>
                ))}
              </div>

              {/* Score distribution */}
                  <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: "12px 14px", marginBottom: 14 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", marginBottom: 8 }}>Score Distribution</div>
                    {distBuckets.map((b) => {
                      const wpct = view.appeared ? Math.round((b.count / view.appeared) * 100) : 0;
                      return (
                        <div key={b.label} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                          <div style={{ width: 64, fontSize: 11, color: "#64748b" }}>{b.label}</div>
                          <div style={{ flex: 1, height: 8, background: "#f1f5f9", borderRadius: 4, overflow: "hidden" }}>
                            <div style={{ width: `${wpct}%`, height: "100%", background: b.color, borderRadius: 4 }} />
                          </div>
                          <div style={{ width: 60, fontSize: 11, color: "#475569", textAlign: "right" }}>{b.count} ({wpct}%)</div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Subject-wise averages for the exam — marks out of the subject's max */}
                  {subjectNames.length > 0 && (
                    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: "12px 14px", marginBottom: 14 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", marginBottom: 10 }}>
                        Subject-wise Average
                        {d.marksPerCorrect ? (
                          <span style={{ fontSize: 11, fontWeight: 500, color: "#94a3b8" }}>
                            {" "}· +{fmtMarks(d.marksPerCorrect)} per correct{d.marksPerWrong ? `, −${fmtMarks(d.marksPerWrong)} per wrong` : ""}
                          </span>
                        ) : null}
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(120px, 1fr))`, gap: 10 }}>
                        {(view.subjectAvg || []).map((s) => (
                          <div key={s.subject} style={{ border: "1px solid #eef2f7", borderRadius: 8, padding: "8px 10px", textAlign: "center" }}>
                            <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.subject}</div>
                            <div style={{ fontSize: 18, fontWeight: 800, color: "#7c3aed" }}>
                              {fmtMarks(s.avgMarks)}
                              <span style={{ fontSize: 12, fontWeight: 600, color: "#94a3b8" }}>/{fmtMarks(s.maxMarks)}</span>
                            </div>
                            <div style={{ fontSize: 11, color: "#94a3b8" }}>{s.pct}%</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Print Results belongs to the Students tab — it prints the student result
                  sheet, and sitting in the modal header it also showed over Question Analysis,
                  where it made no sense. */}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                      Students
                      <span style={{ fontSize: 11, fontWeight: 500, color: "#94a3b8" }}>
                        {" "}· {students.length}{secFilter === "ALL" ? "" : ` in ${secFilter}`}
                      </span>
                    </div>
                    {students.length > 0 && (
                      <button type="button" onClick={this.printResults}
                        title={secFilter === "ALL" ? "Print all student results" : `Print results for ${secFilter}`}
                        style={{ border: "none", background: "#7c3aed", color: "#fff", borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                        <i className="fal fa-print" /> Print Results
                      </button>
                    )}
                  </div>

                  {/* Student results — percentile + per-subject marks (correct/total) */}
                  <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: "8px 4px", maxHeight: 360, overflow: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
                      <thead><tr>
                        <SortTh label="Rank" sortKey="rank" state={this.state.detailSort} onSort={this.setDetailSort} style={th} />
                        <SortTh label="Student" sortKey="name" state={this.state.detailSort} onSort={this.setDetailSort} style={th} />
                        <SortTh label="Mobile" sortKey="mobile" state={this.state.detailSort} onSort={this.setDetailSort} style={th} />
                        <SortTh label="Section" sortKey="sectionName" state={this.state.detailSort} onSort={this.setDetailSort} style={th} />
                        {subjectNames.map((sub) => (
                          <th key={sub} style={{ ...th, textAlign: "center" }} title={`${sub} — marks scored`}>{sub}</th>
                        ))}
                        <SortTh label="Marks" sortKey="marks" state={this.state.detailSort} onSort={this.setDetailSort} style={th} center />
                        <SortTh label="Correct" sortKey="correct" state={this.state.detailSort} onSort={this.setDetailSort} style={th} center />
                        <SortTh label="Wrong" sortKey="wrong" state={this.state.detailSort} onSort={this.setDetailSort} style={th} center />
                        <SortTh label="%" sortKey="pct" state={this.state.detailSort} onSort={this.setDetailSort} style={th} center />
                        <SortTh label="Percentile" sortKey="percentile" state={this.state.detailSort} onSort={this.setDetailSort} style={th} center />
                        <th style={{ ...th, textAlign: "center" }}>Report</th>
                      </tr></thead>
                      <tbody>
                        {students.length === 0 ? (
                          <tr><td colSpan={10 + subjectNames.length} style={{ ...td, textAlign: "center", color: "#94a3b8", padding: 24 }}>No student results.</td></tr>
                        ) : sortRows(students, this.state.detailSort.sortKey, this.state.detailSort.sortDir).map((s) => (
                          <tr key={s.mobile + s.rank} onClick={() => this.openStudentProfile(s)} style={{ cursor: "pointer" }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = "#faf5ff")}
                            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                            title="View student profile">
                            <td style={{ ...td, fontWeight: 700 }}>{s.rank}</td>
                            <td style={{ ...td, fontWeight: 600, color: "#7c3aed", textDecoration: "underline" }}>{s.name}</td>
                            <td style={td}>{s.mobile}</td>
                            <td style={td}>{s.sectionName || "—"}</td>
                            {subjectNames.map((sub) => {
                              const m = (s.subjects || []).find((x) => x.subject === sub);
                              if (!m || !m.total) return <td key={sub} style={{ ...td, textAlign: "center", color: "#cbd5e1" }}>—</td>;
                              return (
                                <td key={sub} style={{ ...td, textAlign: "center" }}
                                  title={`${m.correct} correct, ${m.wrong} wrong of ${m.total}`}>
                                  <span style={{ fontWeight: 700, color: "#0f172a" }}>{fmtMarks(m.marks)}</span>
                                  <span style={{ color: "#94a3b8" }}>/{fmtMarks(m.maxMarks)}</span>
                                </td>
                              );
                            })}
                            <td style={{ ...td, textAlign: "center" }}
                              title={`+${fmtMarks(s.correctMarks)} correct, −${fmtMarks(s.negativeMarks)} negative`}>
                              <span style={{ fontWeight: 800, color: "#7c3aed" }}>{fmtMarks(s.marks)}</span>
                              <span style={{ color: "#94a3b8" }}>/{fmtMarks(s.maxMarks)}</span>
                            </td>
                            <td style={{ ...td, textAlign: "center", color: "#16a34a", fontWeight: 700 }}>{s.correct}</td>
                            <td style={{ ...td, textAlign: "center", color: "#dc2626" }}>{s.wrong}</td>
                            <td style={{ ...td, textAlign: "center", fontWeight: 700, color: "#0f172a" }}>{s.pct}%</td>
                            <td style={{ ...td, textAlign: "center", fontWeight: 700, color: "#2563eb" }}>{s.percentile}</td>
                            <td style={{ ...td, textAlign: "center" }}>
                              {/* stopPropagation: the row itself opens the student profile */}
                              <button type="button" title="Print report card"
                                onClick={(e) => { e.stopPropagation(); this.printReportCard(s, view); }}
                                style={{ border: "1px solid #e5e7eb", background: "#fff", color: "#7c3aed", borderRadius: 7, padding: "4px 9px", fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                                <i className="fal fa-print" /> Card
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>
    );
  };
}

export default withRouter(withApollo(ExamReportsTab));
